import { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import type { FC, CSSProperties } from 'react';
import { Bar, BarChart as RechartsBarChart, CartesianGrid, XAxis, YAxis, Line, LineChart as RechartsLineChart, PieChart as RechartsPieChart, Pie, Cell } from 'recharts';
import { ChartContainer, ChartTooltip, ChartTooltipContent, ChartLegend, ChartLegendContent, type ChartConfig } from './ui/chart';
import { useAnalysisData } from '../hooks/useAnalysisData';
import { useAnalysis } from '../hooks/useAnalysis';
import type { Analysis } from '../services/analysis.service';
import { Loader2Icon, RefreshCw, ArrowUpDown, ArrowUp, ArrowDown, X, Save, Download, Share, Plus, GitBranch, ArrowLeft, Search, Eye, EyeOff, ChevronLeft, ChevronRight } from 'lucide-react';
import { formatValue, formatTickValue, summarizeNumber } from '../services/formatting.service';
import { Button } from './ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Badge } from './ui/badge';
import { analysisService } from '../services/analysis.service';
import BackgroundService from '../services/bg.service';
import { useQueryClient } from '@tanstack/react-query';
import ApiService, { ApiError } from '../services/api.service';
import { SourceIcon } from './SourceIcon';
import { DrilldownOverlay } from './DrilldownOverlay';
import { useFeatureFlag } from '../services/featureFlag.service';
import { FeatureFlag } from '../services/featureFlag.service';
import { useDimensions } from '../services/reportBuilder.service';
import { AnalysisDependencyGraph } from './AnalysisDependencyGraph';
import { useDataExplorer } from '../contexts/DataExplorerContext';
import { SaveChartDialog } from './SaveChartDialog';
import { toastService } from '../services/toast.service';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from './ui/dialog';

interface ChartProps {
  analysisId: string;
  workingAnalysis?: Analysis | null;
  dateRange?: string;
  customDateStart?: string;
  customDateEnd?: string;
  dimensionFilters?: any[]; // Dimension filters from dashboard
  onDataLoaded?: (data: any, config: any) => void;
  onRefreshReady?: (handler: (isApplyOperation?: boolean) => Promise<void>) => void;
  onRefresh?: () => void; // Called when chart refresh starts
  onRefreshFinished?: () => void; // Called when chart refresh finishes
  onSortChange?: (sortOrder: any[] | undefined) => void;
  initialSortOrder?: any[];
  isApplyingChanges?: boolean;
  onApplyChangesComplete?: () => void;
  onCellClick?: (key: string, value: any, row: Record<string, any>, isDimension: boolean) => void;
  onDrillDown?: (updatedAnalysis: Analysis) => void;
  onDimensionFilterNoRefresh?: (updatedAnalysis: Analysis) => void;
  disableAutoPrep?: boolean; // If true, don't automatically trigger prep on 404 errors
  compact?: boolean; // If true, hide axis labels and reduce padding for dashboard embedding
  forceShowPrepLoader?: boolean; // If true, always show "Generating dataset" loader
  refreshDisabled?: boolean; // If true, disable the refresh button
  disableSave?: boolean; // If true, hide the Save button
  disableAddToDashboard?: boolean; // If true, hide the Add to Dashboard button
  enableExploreFromHere?: boolean; // If true, show the Explore from here button
  hideToolbar?: boolean; // If true, hide lineage, download, and sort badges in the top bar
  disableSort?: boolean; // If true, disable table sorting entirely
  componentId?: string; // Dashboard component ID, used for data-testid on the explore button
}

// Helper function to format date values based on x_axis_date_format
// Dates from backend are always in UTC, so we parse and display them as UTC
const formatDateValue = (value: any, format?: string): string => {
  if (!format || !value) {
    return String(value);
  }

  // Try to parse the value as a date in UTC
  let date: Date | null = null;
  
  // Handle different date formats
  if (value instanceof Date) {
    date = value;
  } else if (typeof value === 'string') {
    // For date strings, parse as UTC explicitly
    // If it's an ISO date string like "2025-01-01", parse it as UTC
    if (value.match(/^\d{4}-\d{2}-\d{2}/)) {
      // Date-only string (YYYY-MM-DD), parse as UTC midnight
      const [year, month, day] = value.split('-').map(Number);
      date = new Date(Date.UTC(year, month - 1, day));
    } else {
      // Try parsing as ISO string or other format
      const parsed = new Date(value);
      if (!isNaN(parsed.getTime())) {
        date = parsed;
      }
    }
  } else if (typeof value === 'number') {
    // For timestamps, assume they're already UTC (or convert if needed)
    const parsed = new Date(value);
    if (!isNaN(parsed.getTime())) {
      date = parsed;
    }
  }

  if (!date || isNaN(date.getTime())) {
    return String(value);
  }

  // Use UTC methods to extract date components
  switch (format) {
    case 'datetime_day':
      // UTC date format (MM/DD/YYYY)
      const month = String(date.getUTCMonth() + 1).padStart(2, '0');
      const day = String(date.getUTCDate()).padStart(2, '0');
      const year = date.getUTCFullYear();
      return `${month}/${day}/${year}`;
    case 'datetime_year':
      // Just the year (UTC)
      return String(date.getUTCFullYear());
    case 'datetime_month':
      // "Jan 2025" format (UTC)
      const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      const monthName = monthNames[date.getUTCMonth()];
      return `${monthName} ${date.getUTCFullYear()}`;
    case 'datetime_quarter':
      // "Q1 2025" format (UTC)
      const quarter = Math.floor(date.getUTCMonth() / 3) + 1;
      return `Q${quarter} ${date.getUTCFullYear()}`;
    default:
      return String(value);
  }
};

// Chart colors using CSS variables that support light/dark mode
const CHART_COLORS = [
  'var(--chart-1)',
  'var(--chart-2)',
  'var(--chart-3)',
  'var(--chart-4)',
  'var(--chart-5)',
];

// Sanitize a key for use in CSS variable names
// CSS variables can only contain alphanumeric characters, hyphens, and underscores
export function sanitizeCssVariableName(key: string): string {
  return key.replace(/[^a-zA-Z0-9_-]/g, '_');
}

// Build a chart config keyed by original dimension name so legend lookups work
// even when names contain spaces or special characters.
export function buildPieChartConfig(
  names: string[],
  colors: string[] = CHART_COLORS,
): ChartConfig {
  const config: ChartConfig = {};
  names.forEach((name, index) => {
    if (!config[name]) {
      config[name] = {
        label: name,
        color: colors[index % colors.length],
      };
    }
  });
  return config;
}

// Calculate min, max, and tick interval for chart axes
// This function implements the correct calculation logic for nice axis bounds
function calculateAxisBounds(
  transformedData: Record<string, any>[],
  dataKeys: string[]
): { minValue: number; maxValue: number; tickInterval: number | undefined } {
  let min = Infinity;
  let max = -Infinity;
  transformedData.forEach((entry) => {
    dataKeys.forEach((key) => {
      const value = entry[key];
      if (value != null) {
        const numValue = typeof value === 'number' ? value : parseFloat(String(value));
        if (!isNaN(numValue) && isFinite(numValue)) {
          if (numValue < min) min = numValue;
          if (numValue > max) max = numValue;
        }
      }
    });
  });
  
  // Store the original max value (before any padding)
  const originalMax = max;
  
  // Calculate nice max value using the same logic as bar charts
  let calculatedMax = max;
  if (calculatedMax <= 0 && min <= 0) {
    // If all values are negative or zero, use default
    return { minValue: min !== Infinity ? min * 1.1 : -100, maxValue: 100, tickInterval: undefined };
  }
  
  if (calculatedMax <= 0) {
    calculatedMax = Math.abs(min) || 100;
  }
  
  // Add 10% padding first
  const paddedMax = calculatedMax * 1.1;
  
  // Safety check: if paddedMax is 0 or negative, something went wrong
  if (paddedMax <= 0) {
    return { 
      minValue: min !== Infinity ? min : 0, 
      maxValue: max !== -Infinity ? max * 1.1 : 100, 
      tickInterval: undefined 
    };
  }
  
  // Find the order of magnitude
  const magnitude = Math.pow(10, Math.floor(Math.log10(paddedMax)));
  const normalized = paddedMax / magnitude;
  
  // Round to nearest nice number
  let niceNormalized;
  if (normalized <= 1.5) niceNormalized = 1;
  else if (normalized <= 3) niceNormalized = 2;
  else if (normalized <= 6) niceNormalized = 5;
  else if (normalized <= 7.5) niceNormalized = 6; // Special case for 6000, 60000, etc.
  else niceNormalized = 10;
  
  const niceMax = niceNormalized * magnitude;
  
  // Calculate a nice tick interval (aim for 4-5 ticks)
  // Try different divisors to find one where roundedMax > originalMax
  let bestInterval: number | null = null;
  let bestRoundedMax: number | null = null;
  
  // Try divisors in order: 3, 4, 5, 6, 2, 1.5, 1
  // This will give us progressively smaller intervals (larger roundedMax values)
  for (let divisor of [3, 4, 5, 6, 2, 1.5, 1]) {
    const candidate = niceMax / divisor;
    const candidateMagnitude = Math.pow(10, Math.floor(Math.log10(candidate)));
    const normalized = candidate / candidateMagnitude;
    
    // Prefer intervals that are 1, 2, or 5 times a power of 10
    if (normalized <= 1 || normalized <= 2 || normalized <= 5) {
      const intervalMagnitude = Math.pow(10, Math.floor(Math.log10(candidate)));
      const normalizedInterval = candidate / intervalMagnitude;
      
      // Round to nice interval: 1, 2, 5, 10, 20, 50, etc.
      let niceIntervalNormalized;
      if (normalizedInterval <= 1) niceIntervalNormalized = 1;
      else if (normalizedInterval <= 2) niceIntervalNormalized = 2;
      else if (normalizedInterval <= 5) niceIntervalNormalized = 5;
      else niceIntervalNormalized = 10;
      
      const niceTickInterval = niceIntervalNormalized * intervalMagnitude;
      
      // Round up the max to the next tick
      const roundedMax = Math.ceil(niceMax / niceTickInterval) * niceTickInterval;
      
      // If this roundedMax is higher than originalMax, use it
      if (roundedMax > originalMax) {
        bestInterval = niceTickInterval;
        bestRoundedMax = roundedMax;
        break;
      }
      
      // Otherwise, keep track of the best candidate so far
      if (bestInterval === null || roundedMax > bestRoundedMax!) {
        bestInterval = niceTickInterval;
        bestRoundedMax = roundedMax;
      }
    }
  }
  
  // If we didn't find a good interval, fall back to the first calculation
  if (bestInterval === null || bestRoundedMax === null) {
    // Fallback: use the original logic
    bestInterval = niceMax / 3;
    const intervalMagnitude = Math.pow(10, Math.floor(Math.log10(bestInterval)));
    const normalizedInterval = bestInterval / intervalMagnitude;
    
    let niceIntervalNormalized;
    if (normalizedInterval <= 1) niceIntervalNormalized = 1;
    else if (normalizedInterval <= 2) niceIntervalNormalized = 2;
    else if (normalizedInterval <= 5) niceIntervalNormalized = 5;
    else niceIntervalNormalized = 10;
    
    bestInterval = niceIntervalNormalized * intervalMagnitude;
    bestRoundedMax = Math.ceil(niceMax / bestInterval) * bestInterval;
  }
  
  const niceTickInterval = bestInterval;
  const roundedMax = bestRoundedMax;
  
  // Use roundedMax as the top (not finalMax with padding) as long as it's higher than originalMax
  // If roundedMax is not higher than originalMax, we still use it (it's the best we found)
  const finalMax = roundedMax > originalMax ? roundedMax : originalMax * 1.1;
  
  // For min value, use simple padding if there are negative values
  // If all values are >= 0, start at 0 (like bar charts)
  let finalMin = 0;
  if (min < 0) {
    const minPadding = (finalMax - min) * 0.1 || Math.abs(min) * 0.1 || 1;
    finalMin = min - minPadding;
  }
  
  return {
    minValue: min !== Infinity ? finalMin : -100,
    maxValue: max !== -Infinity ? finalMax : 100,
    tickInterval: min >= 0 ? niceTickInterval : undefined,
  };
}


// Map frontend date range values to backend format
const mapDateRangeToBackend = (dateRange: string): string => {
  const mapping: Record<string, string> = {
    'last_7_days': 'LAST_7_DAYS',
    'last_30_days': 'LAST_30_DAYS',
    'last_90_days': 'LAST_90_DAYS',
    'last_365_days': 'LAST_365_DAYS',
    'this_month': 'THIS_MONTH',
    'last_month': 'LAST_MONTH',
    'this_quarter': 'THIS_QUARTER',
    'last_quarter': 'LAST_QUARTER',
    'this_year': 'THIS_YEAR',
    'last_year': 'LAST_YEAR',
    'all_time': 'ALL_TIME',
    'custom': 'CUSTOM',
  };
  return mapping[dateRange] || dateRange.toUpperCase();
};

// Sort state type: column name -> 'asc' | 'desc' | null
type SortState = Record<string, 'asc' | 'desc' | null>;

export const Chart: FC<ChartProps> = ({ analysisId, workingAnalysis, dateRange, customDateStart, customDateEnd, dimensionFilters, onDataLoaded, onRefreshReady, onSortChange, initialSortOrder, isApplyingChanges = false, onApplyChangesComplete, onCellClick, onDrillDown, onDimensionFilterNoRefresh, disableAutoPrep = false, compact = false, forceShowPrepLoader = false, onRefresh, onRefreshFinished, refreshDisabled = false, disableSave = false, disableAddToDashboard = false, enableExploreFromHere = false, hideToolbar = false, disableSort = false, componentId }) => {
  const queryClient = useQueryClient();
  const { openDataExplorer } = useDataExplorer();
  const allowDrilldowns = useFeatureFlag(FeatureFlag.ALLOW_DRILLDOWNS, false);
  const { data: savedAnalysis, isLoading: isLoadingAnalysis, isSuccess: isAnalysisLoaded } = useAnalysis(analysisId, true); // Include graph
  const { data: allDimensions } = useDimensions();
  
  // Use working analysis if provided, otherwise use saved analysis
  const analysis = workingAnalysis || savedAnalysis;
  
  // Use initialSortOrder from prop if provided, otherwise use savedAnalysis.default_sort_order
  // This ensures we have the sort order as soon as analysis is available, avoiding timing issues
  // Stabilize with useMemo to prevent unnecessary recalculations when the array reference changes
  const effectiveInitialSortOrder = useMemo(() => {
    return initialSortOrder || savedAnalysis?.default_sort_order;
  }, [initialSortOrder, savedAnalysis?.default_sort_order]);
  
  // Drilldown overlay state
  const [drilldownPopoverOpen, setDrilldownPopoverOpen] = useState(false);
  const [drilldownCellData, setDrilldownCellData] = useState<{
    key: string;
    value: any;
    row: Record<string, any>;
    isDimension: boolean;
  } | null>(null);
  const [isPrepping, setIsPrepping] = useState(false);
  const [prepError, setPrepError] = useState<string | null>(null);
  const [dependencyGraph, setDependencyGraph] = useState<any>(null);
  const [showLineage, setShowLineage] = useState(false);
  const [loadingLineage, setLoadingLineage] = useState(false);
  const [showChart, setShowChart] = useState(false); // For fade transition
  const hasAttemptedAutoPrep = useRef(false);
  
  // Extract dependency graph from saved analysis if available
  useEffect(() => {
    if (savedAnalysis && (savedAnalysis as any).dependency_graph) {
      console.log('Chart - Extracting dependency graph from saved analysis:', (savedAnalysis as any).dependency_graph);
      setDependencyGraph((savedAnalysis as any).dependency_graph);
    }
  }, [savedAnalysis]);
  const hasPrepFailed = useRef(false); // Track if prep has failed (hard stop)
  const [sortState, setSortState] = useState<SortState>({});
  const [downloadDialogOpen, setDownloadDialogOpen] = useState(false);
  const [downloadStatus, setDownloadStatus] = useState<'idle' | 'generating' | 'ready' | 'error'>('idle');
  const [downloadUrl, setDownloadUrl] = useState<string | null>(null);
  const [downloadError, setDownloadError] = useState<string | null>(null);
  const lastConfigRef = useRef<any>(null);
  const previousDataRef = useRef<any>(null); // Store previous data for skeleton overlay
  const previousSortOrderRef = useRef<any[] | undefined>(undefined); // Store previous sort order to prevent double fetch
  const previousAnalysisIdRef = useRef<string | undefined>(undefined); // Track previous analysis ID
  const previousAnalysisRef = useRef<Analysis | null>(null); // Track previous analysis to detect what changed
  const sortStateInitializedRef = useRef<boolean>(false); // Track if sort state has been initialized
  const initialSortOrderRef = useRef<any[] | undefined>(undefined); // Store initial sort order to use before sortState is initialized
  const hasDeterminedInitialSortRef = useRef<boolean>(false); // Track if we've determined the initial sort order to prevent double fetches
  
  // Build date range config - use the selected dateRange from dropdown, include custom dates if available
  // This ensures the dropdown selection is what gets sent to the backend
  const dateRangeConfig = useMemo(() => {
    if (!dateRange) return undefined;
    
    const mappedRange = mapDateRangeToBackend(dateRange);
    const config: any = {
      date_range: mappedRange,
      user_timezone: analysis?.user_timezone || 'UTC'
    };
    
    // If it's a custom range, we need the dates from props (preferred) or from the analysis
    // If dates aren't available, don't send CUSTOM (will fall back to analysis default)
    if (mappedRange === 'CUSTOM') {
      const startDate = customDateStart || analysis?.custom_date_range_start;
      const endDate = customDateEnd || analysis?.custom_date_range_end;
      if (startDate && endDate) {
        config.custom_date_range_start = startDate;
        config.custom_date_range_end = endDate;
      } else {
        // Don't send CUSTOM without dates - return undefined to use analysis default
        return undefined;
      }
    }
    
    return config;
  }, [dateRange, customDateStart, customDateEnd, analysis?.user_timezone, analysis?.custom_date_range_start, analysis?.custom_date_range_end]);

  // Convert sort state to backend format
  const sortOrder = useMemo(() => {
    // If analysis ID changed, clear previous sort order
    if (analysis?.id !== previousAnalysisIdRef.current) {
      previousSortOrderRef.current = undefined;
      initialSortOrderRef.current = undefined;
      hasDeterminedInitialSortRef.current = false;
      previousAnalysisIdRef.current = analysis?.id;
    }
    
    if (!analysis) {
      return previousSortOrderRef.current;
    }
    
    // If we don't have config yet, use effectiveInitialSortOrder if available (it's already in backend format)
    // This prevents fetching with null sort, then fetching again with the correct sort
    if (!lastConfigRef.current) {
      if (effectiveInitialSortOrder && effectiveInitialSortOrder.length > 0) {
        // Store it so we can use it consistently
        if (!initialSortOrderRef.current) {
          initialSortOrderRef.current = effectiveInitialSortOrder;
          previousSortOrderRef.current = effectiveInitialSortOrder;
          hasDeterminedInitialSortRef.current = true;
        }
        return initialSortOrderRef.current;
      }
      // If we've already determined there's no initial sort, return undefined consistently
      if (hasDeterminedInitialSortRef.current) {
        return previousSortOrderRef.current;
      }
      // First time: if no effectiveInitialSortOrder, mark as determined and return undefined
      if (!effectiveInitialSortOrder || effectiveInitialSortOrder.length === 0) {
        hasDeterminedInitialSortRef.current = true;
        previousSortOrderRef.current = undefined;
      }
      return previousSortOrderRef.current;
    }
    
    const config = lastConfigRef.current;
    const { dimension_keys = [], metric_keys = [] } = config;
    
    // Build mapping from column names to entity IDs
    const dimensionNameToId: Record<string, string> = {};
    const metricNameToId: Record<string, string> = {};
    
    if (analysis.dimension_ids && dimension_keys) {
      dimension_keys.forEach((name: string, idx: number) => {
        if (analysis.dimension_ids[idx]) {
          dimensionNameToId[name] = analysis.dimension_ids[idx];
        }
      });
    }
    
    if (analysis.measure_ids && metric_keys) {
      metric_keys.forEach((name: string, idx: number) => {
        if (analysis.measure_ids[idx]) {
          metricNameToId[name] = analysis.measure_ids[idx];
        }
      });
    }
    
    // If sortState hasn't been initialized yet but we have effectiveInitialSortOrder, use it directly
    // This prevents fetching with null sort, then fetching again with the correct sort
    if (!sortStateInitializedRef.current && effectiveInitialSortOrder && effectiveInitialSortOrder.length > 0) {
      // Store the initial sort order to use it directly
      if (!initialSortOrderRef.current) {
        initialSortOrderRef.current = effectiveInitialSortOrder;
        previousSortOrderRef.current = effectiveInitialSortOrder;
        return effectiveInitialSortOrder;
      }
      // If we've already stored it, use the stored value to prevent recalculation
      return initialSortOrderRef.current;
    }
    
    // Convert sort state to backend format, preserving order
    const sortEntries = Object.entries(sortState).filter(([_, direction]) => direction !== null);
    if (sortEntries.length === 0) {
      // If sortState is empty but we have effectiveInitialSortOrder and haven't initialized yet, use it
      // This handles the case where user hasn't changed sort but we want to use the saved sort
      if (!sortStateInitializedRef.current && effectiveInitialSortOrder && effectiveInitialSortOrder.length > 0) {
        if (!initialSortOrderRef.current) {
          initialSortOrderRef.current = effectiveInitialSortOrder;
          previousSortOrderRef.current = effectiveInitialSortOrder;
        }
        return initialSortOrderRef.current;
      }
      previousSortOrderRef.current = undefined;
      initialSortOrderRef.current = undefined;
      return undefined;
    }
    
    const backendSortOrder = sortEntries.map(([columnName, direction]) => {
      const isMetric = metricNameToId[columnName] !== undefined;
      const entityId = isMetric ? metricNameToId[columnName] : dimensionNameToId[columnName];
      const entityType = isMetric ? 'METRIC' : 'DIMENSION';
      
      return {
        entity_type: entityType,
        entity_id: entityId,
        ascending: direction === 'asc',
      };
    });
    
    // Store the calculated sort order
    previousSortOrderRef.current = backendSortOrder;
    
    return backendSortOrder;
  }, [sortState, analysis, effectiveInitialSortOrder]);

  // Notify parent of sort changes
  useEffect(() => {
    if (onSortChange) {
      onSortChange(sortOrder);
    }
  }, [sortOrder, onSortChange]);

  // Always try to fetch data when analysis is loaded (unless we're currently prepping)
  // The flow is: try to fetch -> if 404, prep -> then fetch again
  // For SINGLE_VALUE charts, dimensions are optional, so we only require measure_ids
  const shouldFetchData = useMemo(() => {
    const isSingleValue = analysis?.visualization_type === 'SINGLE_VALUE';
    const hasMeasures = !!analysis?.measure_ids?.length;
    const hasDimensions = !!analysis?.dimension_ids?.length;
    
    return Boolean(
      (workingAnalysis ? true : isAnalysisLoaded) &&
      hasMeasures &&
      (isSingleValue || hasDimensions) && // Dimensions are optional for SINGLE_VALUE charts
      !isPrepping // Don't fetch while prepping, we'll fetch after prep completes
    );
  }, [workingAnalysis, isAnalysisLoaded, analysis?.measure_ids?.length, analysis?.dimension_ids?.length, analysis?.visualization_type, isPrepping]);
  
  // Pass dashboard dimension filters through to the backend as-is.
  // The backend's get_final_dimension_filters() handles merging these with
  // the analysis's own dimension_filters, matching the scheduled build path.
  const effectiveDimensionFilters = dimensionFilters;

  const { data, isLoading: isLoadingData, isFetching: isFetchingData, error, refetch } = useAnalysisData({
    analysis: analysis || null,
    enabled: shouldFetchData,
    dateRange: dateRangeConfig,
    sortOrder: sortOrder,
    dimensionFilters: effectiveDimensionFilters,
  }, {
    // Don't retry data endpoint - we fetch once, and if it fails with 404, we prep
    // If it fails with any other error, we show the error without retrying
    retry: false,
  });
  
  // When data loads successfully, show the chart
  // Reset hasAttemptedAutoPrep after transition completes (after a delay to allow fade)
  useEffect(() => {
    if (data?.record && !showChart) {
      setShowChart(true);
      // Reset hasAttemptedAutoPrep after transition completes (500ms fade duration)
      if (hasAttemptedAutoPrep.current) {
        setTimeout(() => {
          hasAttemptedAutoPrep.current = false;
        }, 500);
      }
    }
  }, [data?.record, showChart]);
  
  // Check for errors after prep completes - if data fetch fails after prep, show error
  // But don't overwrite an existing prepError (which may have a better error message from the prep itself)
  useEffect(() => {
    if (!isPrepping && hasAttemptedAutoPrep.current && error && error instanceof ApiError && !prepError) {
      // If we just finished prep and data fetch failed, show error
      // Extract actual error message from ApiError payload
      const errorMessage = error.payload?.msg || error.payload?.exception || error.message || 'Failed to load data after prep';
      setPrepError(errorMessage);
      hasPrepFailed.current = true;
    }
  }, [isPrepping, error, hasAttemptedAutoPrep, prepError]);

  const isLoading = isLoadingAnalysis || isLoadingData || isPrepping;
  
  // Track previous data to show skeleton overlay when reloading
  const hasPreviousData = previousDataRef.current !== null;
  // Show overlay when applying changes and either prepping or loading data, or during normal reload
  const isReloading = ((isLoadingData || isFetchingData) && hasPreviousData && !isLoadingAnalysis && !isPrepping && !isApplyingChanges) || (isApplyingChanges && (isPrepping || isLoadingData || isFetchingData || isLoadingAnalysis));
  
  // Update previous data ref when data changes
  useEffect(() => {
    if (data?.record) {
      previousDataRef.current = data.record;
    }
  }, [data?.record]);

  // Helper function to extract detailed error messages from orchestration
  const extractOrchestrationError = useCallback((orchestration: any): string => {
    // First check the top-level error
    if (orchestration.error) {
      return orchestration.error;
    }

    // Check build_executions for errors
    if (orchestration.build_executions) {
      for (const [key, execution] of Object.entries(orchestration.build_executions)) {
        const exec = execution as any;
        if (exec.error) {
          return exec.error;
        }
        if (exec.build_exception) {
          // build_exception might be a string or an object
          if (typeof exec.build_exception === 'string') {
            return exec.build_exception;
          }
          if (exec.build_exception.message) {
            return exec.build_exception.message;
          }
          return JSON.stringify(exec.build_exception);
        }
      }
    }

    // Check model_statuses for errors
    if (orchestration.model_statuses) {
      for (const [key, status] of Object.entries(orchestration.model_statuses)) {
        const modelStatus = status as any;
        if (modelStatus.error) {
          return modelStatus.error;
        }
      }
    }

    return 'Build failed';
  }, []);

  const pollOrchestrationStatus = useCallback(async (orchestrationId: string) => {
    let status = 'queued';
    let iteration = 0;
    const maxIterations = 120;

    while (['queued', 'started', 'deferred', 'scheduled'].includes(status) && iteration < maxIterations) {
      await new Promise((resolve) => setTimeout(resolve, 2000));

      try {
        const orchestration = await analysisService.getOrchestrationStatus(orchestrationId);
        status = orchestration.status;

        if (status === 'COMPLETE') {
          // Re-fetch orchestration to check for errors even if status is COMPLETE
          const finalOrchestration = await analysisService.getOrchestrationStatus(orchestrationId);
          if (finalOrchestration.status === 'ERROR' || finalOrchestration.error) {
            throw new Error(extractOrchestrationError(finalOrchestration));
          }
          return finalOrchestration;
        } else if (status === 'ERROR') {
          throw new Error(extractOrchestrationError(orchestration));
        }
      } catch (err) {
        if (iteration < maxIterations - 1 && err instanceof Error && !err.message.includes('Build failed')) {
          // Continue polling on transient errors (but not build failures)
          continue;
        }
        throw err;
      }
      iteration++;
    }

    if (iteration >= maxIterations) {
      throw new Error('Max wait time exceeded');
    }
  }, [extractOrchestrationError]);

  const handleAutoPrep = useCallback(async (force: boolean = false, stalenessThresholdSeconds?: number) => {
    if (!analysis || isPrepping || hasPrepFailed.current) return;

    setIsPrepping(true);
    setPrepError(null);

    try {
      // Use the same dateRangeConfig and dimensionFilters that's used for data fetching
      // This ensures consistency between /data and /prep endpoints
      // Pass force=true only when explicitly requested (manual refresh)
      const prepResult = await analysisService.prepAnalyses(
        [analysis],
        effectiveDimensionFilters,
        dateRangeConfig,
        undefined, // skipDateRangeAnalysisIds
        force,
        stalenessThresholdSeconds
      );

      const orchestration = prepResult.record;
      
      // Store dependency graph if available
      console.log('Chart - Prep result:', prepResult);
      console.log('Chart - Dependency graph from prep:', prepResult.dependency_graph);
      if (prepResult.dependency_graph) {
        setDependencyGraph(prepResult.dependency_graph);
        console.log('Chart - Set dependency graph state:', prepResult.dependency_graph);
      } else {
        console.warn('Chart - No dependency graph in prep result');
      }
      
      // If there's a job_id, wait for it using BackgroundService
      if (orchestration.job_id) {
        await BackgroundService.getInstance().waitForJob(
          orchestration.job_id,
          true, // fastest_result
          undefined, // onProgress
          false // ignore_404
        );
        
        // After job completes, re-fetch orchestration to check for errors
        const finalOrchestration = await analysisService.getOrchestrationStatus(orchestration.id);
        if (finalOrchestration.status === 'ERROR' || finalOrchestration.error) {
          throw new Error(extractOrchestrationError(finalOrchestration));
        }
      } else {
        // Otherwise, poll the orchestration status directly
        await pollOrchestrationStatus(orchestration.id);
      }

      // Keep hasAttemptedAutoPrep.current = true to trigger the fade transition
      // Reset it only after data loads successfully
      hasPrepFailed.current = false; // Clear the failure flag on success

      // Invalidate the cached data so React Query knows it's stale.
      // Don't use type: 'active' — the query is currently disabled (isPrepping=true
      // makes shouldFetchData=false), so there are no active queries to target.
      // Once isPrepping is set to false in the finally block, the query re-enables
      // and React Query will automatically refetch because the data is invalidated.
      queryClient.invalidateQueries({ queryKey: ['analysisData', analysis.id] });
    } catch (err) {
      // HARD STOP: Any error from prep means we stop completely
      // Extract the actual error message from different error types
      let errorMessage = 'Failed to prepare analysis';
      if (err instanceof ApiError) {
        // ApiError has a payload with the actual error message
        errorMessage = err.payload?.msg || err.payload?.exception || err.message || 'API error';
      } else if (err instanceof Error) {
        errorMessage = err.message;
      }
      setPrepError(errorMessage);
      hasPrepFailed.current = true; // Set failure flag - this stops all auto-prep attempts
      console.error('Error preparing analysis:', err);

      // Don't retry automatically - this is a hard error
      // User must manually retry via the refresh button
    } finally {
      setIsPrepping(false);
      // Keep dependency graph for lineage view, but don't show it by default after prep
    }
  }, [analysis, dateRangeConfig, effectiveDimensionFilters, queryClient, isPrepping, pollOrchestrationStatus, extractOrchestrationError]);

  // Helper function to check if only visualization_type changed
  const hasOnlyChartTypeChanged = useCallback((prevAnalysis: Analysis | null, currentAnalysis: Analysis | null): boolean => {
    if (!prevAnalysis || !currentAnalysis) return false;
    
    // Check if only visualization_type changed
    const chartTypeChanged = prevAnalysis.visualization_type !== currentAnalysis.visualization_type;
    const measuresChanged = JSON.stringify(prevAnalysis.measure_ids || []) !== JSON.stringify(currentAnalysis.measure_ids || []);
    const dimensionsChanged = JSON.stringify(prevAnalysis.dimension_ids || []) !== JSON.stringify(currentAnalysis.dimension_ids || []);
    const filtersChanged = JSON.stringify(prevAnalysis.dimension_filters || []) !== JSON.stringify(currentAnalysis.dimension_filters || []);
    
    return chartTypeChanged && !measuresChanged && !dimensionsChanged && !filtersChanged;
  }, []);

  // Handle 404 errors by automatically triggering prep
  useEffect(() => {
    // Don't auto-prep if prep has already failed (hard stop) or if we're already prepping
    if (hasPrepFailed.current || isPrepping) {
      // Still update previous analysis ref when analysis changes (even if prepping)
      // This ensures we track the previous value for future comparisons
      if (analysis && !error) {
        previousAnalysisRef.current = analysis;
      }
      return;
    }

    // Auto-prep on 404 errors (data not available)
    // BUT: Skip prep if only chart type changed and we have existing data
    // AND: Skip if disableAutoPrep is true (for dashboard components)
    if (
      error && 
      error instanceof ApiError && 
      error.code === 404 && 
      analysis && 
      !hasAttemptedAutoPrep.current &&
      !disableAutoPrep // Don't auto-prep if disabled (for dashboard components)
    ) {
      // Capture previous analysis BEFORE updating the ref
      // This ensures we compare against the actual previous value
      const prevAnalysis = previousAnalysisRef.current;
      const onlyChartTypeChanged = hasOnlyChartTypeChanged(prevAnalysis, analysis);
      const hasExistingData = previousDataRef.current !== null;
      
      // If only chart type changed and we have existing data, skip prep and just refetch
      if (onlyChartTypeChanged && hasExistingData) {
        // Skip prep, just refetch data
        refetch();
        // Update previous analysis ref after comparison
        previousAnalysisRef.current = analysis;
        return;
      }
      
      // Otherwise, trigger prep as normal
      hasAttemptedAutoPrep.current = true;
      // Pass 1 hour staleness threshold when applying changes from data explorer
      const stalenessThreshold = isApplyingChanges ? 3600 : undefined; // 1 hour = 3600 seconds
      handleAutoPrep(false, stalenessThreshold); // Auto-prep, don't force
      // Update previous analysis ref after handling
      previousAnalysisRef.current = analysis;
    } else if (!error && hasAttemptedAutoPrep.current) {
      // Reset attempt flag when error clears (data loaded successfully)
      hasAttemptedAutoPrep.current = false;
      // Update previous analysis ref
      if (analysis) {
        previousAnalysisRef.current = analysis;
      }
    } else if (analysis && !error) {
      // Update previous analysis ref when analysis changes and there's no error
      // This ensures we track the previous value for future comparisons
      previousAnalysisRef.current = analysis;
    }
  }, [error, analysis, isPrepping, handleAutoPrep, hasOnlyChartTypeChanged, refetch, isApplyingChanges]);

  // Manual refresh handler (for button click) - triggers prep then data fetch
  const handleManualRefresh = useCallback(async (isApplyOperation: boolean = false) => {
    // Reset all flags to allow manual retry
    hasAttemptedAutoPrep.current = false;
    hasPrepFailed.current = false; // Clear the failure flag to allow retry
    setPrepError(null);
    
    // Notify parent that refresh is starting
    onRefresh?.();
    
    try {
      // When applying changes, use staleness threshold instead of force to avoid unnecessary rebuilds
      // Pass 1 hour staleness threshold when applying changes from data explorer or chart detail
      // Otherwise, pass force=true for explicit manual refresh to ensure build happens
      if (isApplyOperation || isApplyingChanges) {
        const stalenessThreshold = 3600; // 1 hour = 3600 seconds
        await handleAutoPrep(false, stalenessThreshold); // Don't force, use staleness threshold
      } else {
        await handleAutoPrep(true, undefined); // Force rebuild for manual refresh
      }
    } finally {
      // Notify parent that refresh is finished (whether success or error)
      onRefreshFinished?.();
    }
  }, [handleAutoPrep, onRefresh, onRefreshFinished, isApplyingChanges]);

  // Expose refresh function to parent
  useEffect(() => {
    if (onRefreshReady) {
      onRefreshReady(handleManualRefresh);
    }
  }, [handleManualRefresh, onRefreshReady]);

  // Wrapper for refresh button click (manual refresh, not apply operation)
  const handleRefreshButtonClick = useCallback(() => {
    handleManualRefresh(false); // false = manual refresh, use force=true
  }, [handleManualRefresh]);

  // Reset initialization flag when analysis changes
  useEffect(() => {
    sortStateInitializedRef.current = false;
  }, [analysis?.id]);

  // Store config in ref and initialize sort state from initialSortOrder when data is available (only once)
  // This must happen synchronously when data loads to prevent double fetches
  useEffect(() => {
    if (data?.record) {
      const config = data.record;
      lastConfigRef.current = config;
      
      // Initialize sort state from effectiveInitialSortOrder immediately when data loads
      // This prevents fetching data with null sort, then fetching again with the correct sort
      if (!sortStateInitializedRef.current && effectiveInitialSortOrder && effectiveInitialSortOrder.length > 0 && analysis) {
        const { dimension_keys = [], metric_keys = [] } = config;
        
        // Build reverse mapping from entity IDs to column names
        const dimensionIdToName: Record<string, string> = {};
        const metricIdToName: Record<string, string> = {};
        
        if (analysis.dimension_ids && dimension_keys) {
          dimension_keys.forEach((name: string, idx: number) => {
            if (analysis.dimension_ids[idx]) {
              dimensionIdToName[analysis.dimension_ids[idx]] = name;
            }
          });
        }
        
        if (analysis.measure_ids && metric_keys) {
          metric_keys.forEach((name: string, idx: number) => {
            if (analysis.measure_ids[idx]) {
              metricIdToName[analysis.measure_ids[idx]] = name;
            }
          });
        }
        
        // Convert backend sort order to frontend sort state
        const newSortState: SortState = {};
        effectiveInitialSortOrder.forEach((sort: any) => {
          const columnName = sort.entity_type === 'METRIC' 
            ? metricIdToName[sort.entity_id]
            : dimensionIdToName[sort.entity_id];
          if (columnName) {
            newSortState[columnName] = sort.ascending ? 'asc' : 'desc';
          }
        });
        
        if (Object.keys(newSortState).length > 0) {
          setSortState(newSortState);
          sortStateInitializedRef.current = true;
          // Clear initialSortOrderRef since we've now initialized sortState
          // This ensures sortOrder will be calculated from sortState going forward
          initialSortOrderRef.current = undefined;
        } else {
          sortStateInitializedRef.current = true;
          initialSortOrderRef.current = undefined;
        }
      } else if (!sortStateInitializedRef.current) {
        // If there's no initial sort order, mark as initialized so we don't keep checking
        sortStateInitializedRef.current = true;
        initialSortOrderRef.current = undefined;
      }
    }
  }, [data?.record, effectiveInitialSortOrder, analysis]);

  // Notify parent component of loaded data when it changes
  // This must be before any early returns to follow Rules of Hooks
  useEffect(() => {
    if (onDataLoaded && data?.record) {
      const config = data.record;
      const chartData = config.data || [];
      if (config && chartData) {
        onDataLoaded(chartData, config);
      }
    }
  }, [onDataLoaded, data?.record]);

  // Revert to chart view and clear stale dependency graph when Apply is clicked
  useEffect(() => {
    if (isApplyingChanges) {
      // Clear the dependency graph to prevent stale graph from flashing
      setDependencyGraph(null);
      // Revert to chart view if lineage is currently shown
      if (showLineage) {
        setShowLineage(false);
      }
    }
  }, [isApplyingChanges, showLineage]);

  // Clear applying changes state when data finishes loading
  useEffect(() => {
    if (isApplyingChanges && !isLoadingData && !isFetchingData && data?.record && onApplyChangesComplete) {
      onApplyChangesComplete();
    }
  }, [isApplyingChanges, isLoadingData, isFetchingData, data?.record, onApplyChangesComplete]);

  // Extract data values and format last refreshed time
  // These hooks must be before any early returns to follow Rules of Hooks
  const lastRefreshed = data?.last_refreshed;
  const upstreamSourceIds = data?.upstream_source_ids || [];

  // Format last refreshed time
  const formattedLastRefreshed = useMemo(() => {
    if (!lastRefreshed) return null;
    try {
      const date = new Date(lastRefreshed);
      const now = new Date();
      const diffMs = now.getTime() - date.getTime();
      const diffMins = Math.floor(diffMs / 60000);
      const diffHours = Math.floor(diffMs / 3600000);
      const diffDays = Math.floor(diffMs / 86400000);

      const full = date.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });

      let short: string;
      if (diffMins < 1) short = 'Just now';
      else if (diffMins < 60) short = `${diffMins}m ago`;
      else if (diffHours < 24) short = `${diffHours}h ago`;
      else if (diffDays < 7) short = `${diffDays}d ago`;
      else short = date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined
      });

      return { short, full };
    } catch {
      return null;
    }
  }, [lastRefreshed]);

  // Handle sort toggle for table - must be before early returns
  const handleSortToggle = useCallback((columnName: string) => {
    setSortState((prev) => {
      const currentDirection = prev[columnName];
      let newDirection: 'asc' | 'desc' | null;
      
      // Determine new direction based on current state
      if (currentDirection === null || currentDirection === undefined) {
        newDirection = 'asc';
      } else if (currentDirection === 'asc') {
        newDirection = 'desc';
      } else if (currentDirection === 'desc') {
        newDirection = null;
      } else {
        // Fallback: if somehow we have an invalid state, default to 'asc'
        newDirection = 'asc';
      }
      
      const newState = { ...prev };
      if (newDirection === null) {
        delete newState[columnName];
      } else {
        newState[columnName] = newDirection;
      }
      
      return newState;
    });
  }, []);

  // Handle removing a sort - must be before early returns
  const handleRemoveSort = useCallback((columnName: string) => {
    setSortState((prev) => {
      const newState = { ...prev };
      delete newState[columnName];
      return newState;
    });
  }, []);

  // Get sort badges for table charts - must be before early returns
  const sortBadges = useMemo(() => {
    if (!data?.record || data.record.render_type !== 'table') return null;
    
    const sortEntries = Object.entries(sortState).filter(([_, direction]) => direction !== null);
    if (sortEntries.length === 0) return null;
    
    return sortEntries.map(([columnName, direction]) => (
      <Badge key={columnName} variant="outline" className="gap-1.5">
        <span>{columnName}</span>
        {direction === 'asc' ? (
          <ArrowUp className="h-3 w-3" />
        ) : (
          <ArrowDown className="h-3 w-3" />
        )}
        <button
          onClick={() => handleRemoveSort(columnName)}
          className="ml-1 hover:bg-muted rounded-full p-0.5"
          title="Remove sort"
        >
          <X className="h-3 w-3" />
        </button>
      </Badge>
    ));
  }, [sortState, data?.record, handleRemoveSort]);

  // Handle cell click for drilldown - must be before early returns
  const handleCellClick = useCallback((key: string, value: any, row: Record<string, any>, isDimension: boolean) => {
    // If drilldowns are disabled via feature flag, do nothing
    if (!allowDrilldowns) return;

    // Always show drilldown overlay when clicking on bars or cells
    // If onCellClick is provided as a legacy callback, call it but still show overlay
    if (onCellClick) {
      onCellClick(key, value, row, isDimension);
    }
    // Always show drilldown overlay
    setDrilldownCellData({ key, value, row, isDimension });
    setDrilldownPopoverOpen(true);
  }, [onCellClick, allowDrilldowns]);

  // Handle dimension filter drilldown
  const handleDimensionFilter = useCallback(() => {
    if (!drilldownCellData || !analysis || !lastConfigRef.current) return;
    
    const { key, value } = drilldownCellData;
    const config = lastConfigRef.current;
    const { dimension_keys = [], dimension_key } = config;
    
    // For pie charts, use dimension_key (singular) instead of dimension_keys array
    let dimensionIndex = -1;
    let dimensionId: string | undefined;
    
    if (dimension_key && key === dimension_key) {
      // Pie chart: dimension_key matches the clicked key
      dimensionId = analysis.dimension_ids[0]; // Pie charts have exactly one dimension
      dimensionIndex = 0;
    } else {
      // Bar/line/table charts: use dimension_keys array
      dimensionIndex = dimension_keys.indexOf(key);
      if (dimensionIndex >= 0 && analysis.dimension_ids[dimensionIndex]) {
        dimensionId = analysis.dimension_ids[dimensionIndex];
      }
    }
    
    if (dimensionIndex < 0 || !dimensionId) return;
    
    const dimension = allDimensions?.find(d => d.id === dimensionId);
    const dimensionName = dimension?.name || key;

    const newDimensionFilters = [
      ...(analysis.dimension_filters || []),
      {
        dimension_id: dimensionId,
        comparator: 'IN',
        value: [String(value)],
      },
    ];

    const updatedAnalysis: Analysis = {
      ...analysis,
      name: `${analysis.name || 'Chart'} - ${dimensionName} = ${value}`,
      visualization_type: 'TABLE', // Always use TABLE when drilling down
      dimension_filters: newDimensionFilters,
      limit: undefined,
    };

    // If onDrillDown is provided (legacy support for ChartDetail page), use it
    if (onDrillDown) {
      onDrillDown(updatedAnalysis);
    } else {
      // Otherwise, open DataExplorer using context
      openDataExplorer({
        analysis: updatedAnalysis,
        originalAnalysisId: analysisId,
        dateRange,
        customDateStart,
        customDateEnd,
      });
    }
  }, [drilldownCellData, analysis, allDimensions, onDrillDown, openDataExplorer, analysisId, dateRange, customDateStart, customDateEnd]);

  // Handle dimension filter without refresh - just adds to filter list
  const handleDimensionFilterNoRefresh = useCallback(() => {
    if (!drilldownCellData || !analysis || !lastConfigRef.current || !onDimensionFilterNoRefresh) return;

    const { key, value } = drilldownCellData;
    const config = lastConfigRef.current;
    const { dimension_keys = [], dimension_key } = config;

    // For pie charts, use dimension_key (singular) instead of dimension_keys array
    let dimensionIndex = -1;
    let dimensionId: string | undefined;

    if (dimension_key && key === dimension_key) {
      // Pie chart: dimension_key matches the clicked key
      dimensionId = analysis.dimension_ids[0]; // Pie charts have exactly one dimension
      dimensionIndex = 0;
    } else {
      // Bar/line/table charts: use dimension_keys array
      dimensionIndex = dimension_keys.indexOf(key);
      if (dimensionIndex >= 0 && analysis.dimension_ids[dimensionIndex]) {
        dimensionId = analysis.dimension_ids[dimensionIndex];
      }
    }

    if (dimensionIndex < 0 || !dimensionId) return;

    const newDimensionFilters = [
      ...(analysis.dimension_filters || []),
      {
        dimension_id: dimensionId,
        comparator: 'IN',
        value: [String(value)],
      },
    ];

    const updatedAnalysis: Analysis = {
      ...analysis,
      dimension_filters: newDimensionFilters,
    };

    onDimensionFilterNoRefresh(updatedAnalysis);
  }, [drilldownCellData, analysis, onDimensionFilterNoRefresh]);

  // Handle metric drilldown
  const handleMetricDrilldown = useCallback((selectedDimensionId: string, selectedFilterDimensionIds: string[]) => {
    if (!drilldownCellData || !analysis || !lastConfigRef.current || !allDimensions) return;
    
    const { row } = drilldownCellData;
    const selectedDimension = allDimensions.find(d => d.id === selectedDimensionId);
    if (!selectedDimension) return;

    const config = lastConfigRef.current;
    // For bar/line charts, construct dimension_keys from x_axis_key and group_by_key
    let dimension_keys = config.dimension_keys || [];
    if (!dimension_keys.length && (config.x_axis_key || config.group_by_key)) {
      dimension_keys = [];
      if (config.x_axis_key) dimension_keys.push(config.x_axis_key);
      if (config.group_by_key) dimension_keys.push(config.group_by_key);
    }
    
    // Create new dimension list with the selected dimension added
    const newDimensionIds = [...analysis.dimension_ids];
    if (!newDimensionIds.includes(selectedDimensionId)) {
      newDimensionIds.push(selectedDimensionId);
    }

    // Create dimension filters from the row values - only for selected dimensions
    const newDimensionFilters = [...(analysis.dimension_filters || [])];
    
    // Add filters only for selected dimensions based on row values
    dimension_keys.forEach((dimKey: string, idx: number) => {
      const dimId = analysis.dimension_ids[idx];
      // Only add filter if this dimension is selected
      if (dimId && row[dimKey] != null && selectedFilterDimensionIds.includes(dimId)) {
        // Check if filter already exists for this dimension
        const existingFilterIndex = newDimensionFilters.findIndex(f => f.dimension_id === dimId);
        if (existingFilterIndex >= 0) {
          // Update existing filter to include this value
          const existingFilter = newDimensionFilters[existingFilterIndex];
          if (existingFilter.comparator === 'IN' || existingFilter.comparator === 'EQUALS') {
            const existingValues = Array.isArray(existingFilter.value) 
              ? existingFilter.value 
              : existingFilter.value ? [existingFilter.value] : [];
            if (!existingValues.includes(String(row[dimKey]))) {
              newDimensionFilters[existingFilterIndex] = {
                ...existingFilter,
                comparator: 'IN',
                value: [...existingValues, String(row[dimKey])],
              };
            }
          }
        } else {
          // Add new filter
          newDimensionFilters.push({
            dimension_id: dimId,
            comparator: 'IN',
            value: [String(row[dimKey])],
          });
        }
      }
    });

    // Always use TABLE visualization type when drilling down
    const updatedAnalysis: Analysis = {
      ...analysis,
      name: `${analysis.name || 'Chart'} - Drilldown`,
      visualization_type: 'TABLE',
      dimension_ids: newDimensionIds,
      dimension_filters: newDimensionFilters,
      limit: undefined,
    };

    // If onDrillDown is provided (legacy support for ChartDetail page), use it
    if (onDrillDown) {
      onDrillDown(updatedAnalysis);
    } else {
      // Otherwise, open DataExplorer using context
      openDataExplorer({
        analysis: updatedAnalysis,
        originalAnalysisId: analysisId,
        dateRange,
        customDateStart,
        customDateEnd,
      });
    }
  }, [drilldownCellData, analysis, allDimensions, onDrillDown, openDataExplorer, analysisId, dateRange, customDateStart, customDateEnd]);

  // Get row dimensions for metric drilldown
  const getRowDimensions = useCallback((): Array<{ dimensionId: string; dimensionName: string; value: any }> => {
    if (!drilldownCellData || !analysis || !lastConfigRef.current || !allDimensions) return [];
    
    const { row } = drilldownCellData;
    const config = lastConfigRef.current;
    
    // For pie charts, use dimension_key (singular) instead of dimension_keys array
    let dimension_keys = config.dimension_keys || [];
    if (!dimension_keys.length) {
      if (config.dimension_key) {
        // Pie chart: use dimension_key
        dimension_keys = [config.dimension_key];
      } else if (config.x_axis_key || config.group_by_key) {
        // Bar/line charts: construct dimension_keys from x_axis_key and group_by_key
        dimension_keys = [];
        if (config.x_axis_key) dimension_keys.push(config.x_axis_key);
        if (config.group_by_key) dimension_keys.push(config.group_by_key);
      }
    }
    
    const rowDimensions: Array<{ dimensionId: string; dimensionName: string; value: any }> = [];
    
    dimension_keys.forEach((dimKey: string, idx: number) => {
      const dimId = analysis.dimension_ids[idx];
      if (dimId && row[dimKey] != null) {
        const dimension = allDimensions?.find(d => d.id === dimId);
        rowDimensions.push({
          dimensionId: dimId,
          dimensionName: dimension?.name || dimKey,
          value: row[dimKey],
        });
      }
    });
    
    return rowDimensions;
  }, [drilldownCellData, analysis, allDimensions]);

  // Get dimension name from key
  const getDimensionNameFromKey = useCallback((key: string): string => {
    if (!analysis || !lastConfigRef.current) return key;
    const { dimension_keys = [] } = lastConfigRef.current;
    const dimensionIndex = dimension_keys.indexOf(key);
    if (dimensionIndex >= 0 && analysis.dimension_ids[dimensionIndex]) {
      const dimensionId = analysis.dimension_ids[dimensionIndex];
      const dimension = allDimensions?.find(d => d.id === dimensionId);
      return dimension?.name || key;
    }
    return key;
  }, [analysis, allDimensions]);

  // Save dialog state
  const [isSaveDialogOpen, setIsSaveDialogOpen] = useState(false);

  // Handle save button click - show dialog
  const handleSave = useCallback(() => {
    if (!analysis) return;
    setIsSaveDialogOpen(true);
  }, [analysis]);

  // Actually save the analysis
  const handleSaveAnalysis = useCallback(async (options: { mode: 'overwrite' | 'new'; name: string }) => {
    if (!analysis) {
      throw new Error('No analysis to save');
    }

    const { mode, name } = options;

    try {
      // Get the current sort order (computed from sortState)
      const currentSortOrder = sortOrder;

      if (mode === 'overwrite' && analysisId && analysisId) {
        // Update existing analysis with PATCH
        const updates: Partial<Analysis> = {
          name,
          visualization_type: analysis.visualization_type,
          dimension_ids: analysis.dimension_ids || [],
          measure_ids: analysis.measure_ids || [],
          dimension_filters: analysis.dimension_filters || [],
          date_range: analysis.date_range,
          custom_date_range_start: analysis.custom_date_range_start,
          custom_date_range_end: analysis.custom_date_range_end,
          default_sort_order: currentSortOrder || analysis.default_sort_order,
          custom_parameters: analysis.custom_parameters,
          limit: analysis.limit,
          ephemeral: false,
        };

        await analysisService.update(analysisId, updates as any);

        // Invalidate the analyses list cache so the chart shows up immediately
        queryClient.invalidateQueries({ queryKey: ['analyses'] });

        // Show success toast
        toastService.success('Chart updated successfully');
      } else {
        // Create a new analysis
        const analysisToSave: Omit<Analysis, 'id' | 'last_updated'> = {
          name,
          visualization_type: analysis.visualization_type,
          dimension_ids: analysis.dimension_ids || [],
          measure_ids: analysis.measure_ids || [],
          dimension_filters: analysis.dimension_filters || [],
          date_range: analysis.date_range,
          custom_date_range_start: analysis.custom_date_range_start,
          custom_date_range_end: analysis.custom_date_range_end,
          default_sort_order: currentSortOrder || analysis.default_sort_order,
          custom_parameters: analysis.custom_parameters,
          limit: analysis.limit,
        };

        // Include ephemeral: false in the request body
        await analysisService.create({
          ...analysisToSave,
          ephemeral: false,
        } as any);

        // Invalidate the analyses list cache so the chart shows up immediately
        queryClient.invalidateQueries({ queryKey: ['analyses'] });

        // Show success toast
        toastService.success('Chart saved successfully', {
          description: 'It is now available on the Analytics page.',
        });
      }
    } catch (error) {
      console.error('Failed to save chart:', error);
      toastService.error('Failed to save chart', {
        description: error instanceof Error ? error.message : 'An unexpected error occurred',
      });
      throw error;
    }
  }, [analysis, sortOrder, analysisId, queryClient]);

  const handleAddToDashboard = useCallback(() => {
    // No-op for now
  }, []);

  const handleDownload = useCallback(async () => {
    if (!analysis) return;

    // Reset state and open dialog
    setDownloadStatus('generating');
    setDownloadUrl(null);
    setDownloadError(null);
    setDownloadDialogOpen(true);

    try {
      // Queue the download job - pass node_id from data response for reliable node lookup
      const result = await analysisService.queueDownload(analysis, {
        nodeId: data?.node_id,  // Use the same node that provided the chart data
        sortOrder,
        dateRange: dateRangeConfig,
        dimensionFilters: effectiveDimensionFilters,
      });

      if (!result.ok || !result.job_id) {
        throw new Error('Failed to start download');
      }

      // Wait for the job to complete
      const jobResult = await BackgroundService.getInstance().waitForJob(result.job_id);

      if (jobResult?.url) {
        setDownloadUrl(jobResult.url);
        setDownloadStatus('ready');
      } else {
        throw new Error('No download URL returned');
      }
    } catch (error) {
      console.error('Download failed:', error);
      setDownloadError(error instanceof Error ? error.message : 'Download failed');
      setDownloadStatus('error');
    }
  }, [analysis, data?.node_id, sortOrder, dateRangeConfig, effectiveDimensionFilters]);

  const handleShare = useCallback(() => {
    // No-op for now
  }, []);

  const handleExploreFromHere = useCallback(() => {
    if (!analysis) return;
    
    openDataExplorer({
      analysis: { ...analysis, limit: undefined },
      originalAnalysisId: analysisId,
      dateRange,
      customDateStart,
      customDateEnd,
    });
  }, [analysis, analysisId, dateRange, customDateStart, customDateEnd, openDataExplorer]);

  // If forceShowPrepLoader is true, always show the loader
  if (forceShowPrepLoader) {
    return (
      <div className="chart-wrapper h-full w-full min-h-0 flex-1 flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <Loader2Icon className="w-8 h-8 animate-spin text-primary" />
          <div className="text-sm font-medium text-foreground">
            Generating dataset
          </div>
        </div>
      </div>
    );
  }

  // Only show full loading spinner if we don't have previous data
  // If we have previous data and are reloading, show skeleton overlay instead
  if (isLoading && !hasPreviousData) {
    return (
      <div className="chart-wrapper h-full w-full min-h-0 flex-1 flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <Loader2Icon className="w-8 h-8 animate-spin text-primary" />
          <div className="text-sm font-medium text-foreground">
            {isPrepping ? 'Generating dataset' : 'Loading chart'}
          </div>
        </div>
      </div>
    );
  }

  // Show prep error first (this is a hard error that stops everything)
  if (prepError) {
    return (
      <div className={compact ? 'p-2' : ''}>
        <div className="p-4 bg-destructive/10 border border-destructive/20 rounded">
          <p className="text-destructive text-sm font-semibold mb-2">
            Error preparing chart data
          </p>
          <p className="text-destructive/90 text-sm">
            {prepError}
          </p>
          <Button
            variant="outline"
            size="sm"
            className="mt-3"
            onClick={handleRefreshButtonClick}
            disabled={isPrepping}
          >
            {isPrepping ? 'Rebuilding...' : 'Rebuild dataset'}
          </Button>
        </div>
      </div>
    );
  }

  // Show other errors (but not 404s, as those trigger prep)
  if (error && !(error instanceof ApiError && error.code === 404)) {
    // In development mode, show detailed error from API payload if available
    const detailedError = import.meta.env.DEV && error instanceof ApiError && error.payload?.msg
      ? error.payload.msg
      : (error instanceof Error ? error.message : 'Unknown error');

    return (
      <div className={compact ? 'p-2' : ''}>
        <div className="p-4 bg-destructive/10 border border-destructive/20 rounded">
          <p className="text-destructive text-sm">
            Error loading chart: {detailedError}
          </p>
          <Button
            variant="outline"
            size="sm"
            className="mt-3"
            onClick={handleRefreshButtonClick}
            disabled={isPrepping}
          >
            {isPrepping ? 'Rebuilding...' : 'Rebuild dataset'}
          </Button>
        </div>
      </div>
    );
  }

  // Use previous data if current data is loading (for skeleton overlay)
  const displayConfig = data?.record || previousDataRef.current;
  const displayData = displayConfig?.data || [];

  if (!displayConfig) {
    return (
      <div className={`${compact ? 'p-4' : ''} chart-wrapper h-full w-full min-h-0 flex-1 flex items-center justify-center`}>
        <div className="p-4 bg-muted/50 border border-border rounded">
          <p className="text-muted-foreground text-sm">No data available for the provided date range &amp; filters</p>
        </div>
      </div>
    );
  }

  const config = displayConfig;
  const chartData = displayData;

  // Render based on chart type
  let chartContent;
  switch (config.render_type) {
    case 'bar':
      chartContent = <BarChart config={config} data={chartData} analysis={analysis} onCellClick={handleCellClick} allDimensions={allDimensions} lastConfigRef={lastConfigRef} compact={compact} />;
      break;
    case 'line':
      chartContent = <LineChart config={config} data={chartData} compact={compact} />;
      break;
    case 'pie':
      chartContent = <PieChart config={config} data={chartData} analysis={analysis} onCellClick={handleCellClick} compact={compact} />;
      break;
    case 'single_value':
      chartContent = <SingleValueChart config={config} data={chartData} analysis={analysis} />;
      break;
    case 'table':
      chartContent = <TableChart config={config} data={chartData} sortState={disableSort ? undefined : sortState} onSortToggle={disableSort ? undefined : handleSortToggle} analysis={analysis} onCellClick={handleCellClick} compact={compact} />;
      break;
    case 'pivot_table':
      chartContent = <PivotTableChart config={config} data={chartData} analysis={analysis} onCellClick={handleCellClick} />;
      break;
    case 'ltv_pivot_table':
    case 'PREMIUM_LTV_PIVOT_TABLE':
      chartContent = <LTVPivotTableChart config={config} analysis={analysis} />;
      break;
    default:
      return (
        <div>
          <p className="text-warning text-sm">
            Unsupported chart type: {config.render_type}
          </p>
        </div>
      );
  }

  const hasFooterContent = true;

  // Determine what to show: dependency graph while prepping or when lineage is toggled
  // Only transition (fade) when prep completes and we're switching to chart
  // No transition when toggling lineage view
  const isTransitioning = showChart && dependencyGraph && !isPrepping && !showLineage && hasAttemptedAutoPrep.current;
  const shouldShowGraph = (isPrepping && dependencyGraph) || showLineage || isTransitioning;
  const shouldShowChart = !isPrepping && showChart && !showLineage && data?.record;

  return (
    <div className="chart-wrapper h-full w-full max-w-full min-w-0 flex flex-col overflow-hidden relative">
      {/* Dependency Graph - shown while prepping or when lineage is toggled */}
      {shouldShowGraph && (
        <div
          className={`flex-1 flex flex-col overflow-hidden ${
            isTransitioning
              ? 'transition-opacity duration-500 ease-in-out opacity-0 absolute inset-0 pointer-events-none'
              : 'opacity-100'
          }`}
        >
          <div className="flex items-center justify-between px-4 py-2 border-b">
            <div className="text-sm font-medium text-foreground">
              {isPrepping ? 'Generating dataset...' : 'Dependency Graph'}
            </div>
            <div className="flex items-center gap-2">
              {isPrepping && <Loader2Icon className="w-4 h-4 animate-spin text-primary" />}
              {showLineage && !isPrepping && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowLineage(false)}
                  className="h-7 px-2 text-xs"
                  title="Back to Chart"
                >
                  <ArrowLeft className="h-3.5 w-3.5 mr-1.5" />
                  Back to Chart
                </Button>
              )}
            </div>
          </div>
          <div className="flex-1 min-h-0">
            {dependencyGraph ? (
              <AnalysisDependencyGraph
                graph={dependencyGraph}
                className="h-full"
                animated={isPrepping} // Only animate while prepping
              />
            ) : loadingLineage ? (
              <div className="flex items-center justify-center h-full">
                <Loader2Icon className="w-6 h-6 animate-spin text-primary" />
              </div>
            ) : null}
          </div>
        </div>
      )}
      
      {/* Chart Content - shown after prep completes */}
      {shouldShowChart && (
        <div 
          className={`flex-1 flex flex-col overflow-hidden ${
            isTransitioning 
              ? 'transition-opacity duration-500 ease-in-out opacity-100' 
              : 'opacity-100'
          }`}
        >
          {isReloading ? (
            // Show centered loader during reload
            <div className="flex-1 flex items-center justify-center">
              <div className="flex flex-col items-center gap-3">
                <Loader2Icon className="w-8 h-8 animate-spin text-primary" />
                <div className="text-sm font-medium text-foreground">
                  Loading chart
                </div>
              </div>
            </div>
          ) : (
            <>
              {/* Top bar with sort params on left and action buttons on right */}
              {!hideToolbar && (
              <div className={`flex items-center justify-between border-b gap-4 min-w-0 ${compact ? 'p-2' : 'mb-4 pb-3'}`}>
                {/* Sort badges on the left */}
                {!disableSort && sortBadges ? (
                  <div className="flex items-center gap-1.5 flex-shrink-0">
                    {sortBadges}
                  </div>
                ) : (
                  <div className="flex-1" />
                )}
                {/* Action buttons on the right */}
                <div className="flex items-center gap-1.5 flex-shrink-0">
                  {enableExploreFromHere && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={handleExploreFromHere}
                      className="h-7 px-2 text-xs"
                      title="Explore from here"
                      data-testid={`afh_nav-${componentId || analysis?.id || ''}`}
                    >
                      <Search className="h-3.5 w-3.5" />
                    </Button>
                  )}
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={async () => {
                      if (showLineage) {
                        setShowLineage(false);
                        return;
                      }
                      setShowLineage(true);
                      // Fetch graph on demand if not already loaded
                      if (!dependencyGraph && analysisId) {
                        setLoadingLineage(true);
                        try {
                          const result = await ApiService.getInstance().request(
                            'GET',
                            `/analyses/${analysisId}`,
                            { include_graph: 'true' }
                          ) as any;
                          if (result.dependency_graph) {
                            setDependencyGraph(result.dependency_graph);
                          } else {
                            console.warn('No dependency_graph returned from backend. Response keys:', Object.keys(result));
                          }
                        } catch (e) {
                          console.error('Failed to load lineage:', e);
                        } finally {
                          setLoadingLineage(false);
                        }
                      }
                    }}
                    className="h-7 px-2 text-xs"
                    title="View lineage"
                  >
                    <GitBranch className="h-3.5 w-3.5" />
                  </Button>
                  {!disableSave && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={handleSave}
                      className="h-7 px-2 text-xs"
                      title="Save"
                    >
                      <Save className="h-3.5 w-3.5" />
                    </Button>
                  )}
                  {/* Add to Dashboard - coming in a later release
                  {!disableAddToDashboard && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={handleAddToDashboard}
                      className="h-7 px-2 text-xs"
                      title="Add to Dashboard"
                    >
                      <Plus className="h-3.5 w-3.5" />
                    </Button>
                  )}
                  */}
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleDownload}
                    className="h-7 px-2 text-xs"
                    title="Download"
                  >
                    <Download className="h-3.5 w-3.5" />
                  </Button>
                  {/* Share - coming in a later release
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleShare}
                    className="h-7 px-2 text-xs"
                    title="Share"
                  >
                    <Share className="h-3.5 w-3.5" />
                  </Button>
                  */}
                </div>
              </div>
              )}
          <div className="flex-1 min-h-0 min-w-0 relative overflow-hidden">
            <div className="h-full w-full min-w-0 max-h-full overflow-x-auto overflow-y-auto">
              {chartContent}
            </div>
          </div>
          {/* Footer with updated time, refresh button, and data source icons */}
          {hasFooterContent && (
            <div className={`flex items-center justify-between border-t gap-4 min-w-0 ${compact ? 'p-2' : 'mt-4 pt-3'}`}>
              <div className="flex items-center gap-3 flex-shrink-0">
                {formattedLastRefreshed && (
                  <span className="text-xs text-gray-500" title={`Updated ${formattedLastRefreshed.full}`}>
                    {formattedLastRefreshed.short}
                  </span>
                )}
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={handleRefreshButtonClick}
                    disabled={isPrepping || refreshDisabled}
                    className="h-6 w-6 text-gray-500 hover:text-gray-700"
                    title="Refresh"
                  >
                    <RefreshCw className={`h-3 w-3 ${isPrepping ? 'animate-spin' : ''}`} />
                  </Button>
              </div>
              {upstreamSourceIds.length > 0 && (
                <div className="flex items-center gap-1.5 flex-shrink-0">
                  {upstreamSourceIds.map((sourceId) => (
                    <SourceIcon 
                      key={sourceId} 
                      sourceId={sourceId} 
                      size={20}
                      className="cursor-help"
                    />
                  ))}
                </div>
              )}
            </div>
            )}
          </>
        )}
        </div>
      )}
      
      {/* Show loader if we're prepping but don't have graph yet, or if we're loading and don't have chart yet */}
      {!shouldShowGraph && !shouldShowChart && (
        <div className="flex-1 flex items-center justify-center">
          <div className="flex flex-col items-center gap-3">
            <Loader2Icon className="w-8 h-8 animate-spin text-primary" />
            <div className="text-sm font-medium text-foreground">
              {isPrepping ? 'Generating dataset...' : 'Loading chart'}
            </div>
          </div>
        </div>
      )}
      
      {/* Drilldown Overlay - Always show when drilldownCellData is set */}
      {drilldownCellData && lastConfigRef.current && (
        <DrilldownOverlay
          open={drilldownPopoverOpen}
          onOpenChange={setDrilldownPopoverOpen}
          isDimension={drilldownCellData.isDimension}
          dimensionName={drilldownCellData.isDimension ? getDimensionNameFromKey(drilldownCellData.key) : undefined}
          dimensionValue={drilldownCellData.isDimension ? drilldownCellData.value : undefined}
          metricName={!drilldownCellData.isDimension ? drilldownCellData.key : undefined}
          availableDimensions={allDimensions || []}
          currentDimensionIds={analysis?.dimension_ids || []}
          rowDimensions={getRowDimensions()}
          onDimensionFilter={handleDimensionFilter}
          onDimensionFilterNoRefresh={onDimensionFilterNoRefresh ? handleDimensionFilterNoRefresh : undefined}
          onMetricDrilldown={handleMetricDrilldown}
        />
      )}

      {/* Save Chart Dialog */}
      <SaveChartDialog
        open={isSaveDialogOpen}
        onOpenChange={setIsSaveDialogOpen}
        defaultName={analysis?.name || ''}
        isExistingChart={!!analysisId}
        onSave={handleSaveAnalysis}
      />

      {/* Download Dialog */}
      <Dialog
        open={downloadDialogOpen}
        onOpenChange={(open) => {
          if (!open) {
            // Reset state when dialog closes
            setDownloadStatus('idle');
            setDownloadUrl(null);
            setDownloadError(null);
          }
          setDownloadDialogOpen(open);
        }}
      >
        <DialogContent onInteractOutside={(e) => e.preventDefault()}>
          <DialogHeader>
            <DialogTitle>Download Data</DialogTitle>
            <DialogDescription>
              {downloadStatus === 'generating' && 'Generating your CSV file...'}
              {downloadStatus === 'ready' && 'Your file is ready to download.'}
              {downloadStatus === 'error' && 'There was an error generating your file.'}
            </DialogDescription>
          </DialogHeader>
          <DialogBody className="py-4 flex flex-col items-center gap-4">
            {downloadStatus === 'generating' && (
              <div className="flex flex-col items-center gap-3">
                <Loader2Icon className="w-8 h-8 animate-spin text-primary" />
                <p className="text-sm text-muted-foreground">
                  This may take a moment for large datasets...
                </p>
              </div>
            )}
            {downloadStatus === 'ready' && downloadUrl && (
              <Button asChild>
                <a href={downloadUrl} download>
                  <Download className="h-4 w-4 mr-2" />
                  Download CSV
                </a>
              </Button>
            )}
            {downloadStatus === 'error' && (
              <div className="text-center">
                <p className="text-sm text-destructive mb-4">
                  {downloadError || 'An unexpected error occurred'}
                </p>
                <Button variant="outline" onClick={() => setDownloadDialogOpen(false)}>
                  Close
                </Button>
              </div>
            )}
          </DialogBody>
        </DialogContent>
      </Dialog>
    </div>
  );
};

interface BarChartProps {
  config: any;
  data: Record<string, any>[];
  analysis?: Analysis | null;
  onCellClick?: (key: string, value: any, row: Record<string, any>, isDimension: boolean) => void;
  allDimensions?: any[];
  lastConfigRef?: React.MutableRefObject<any>;
  compact?: boolean;
}

const BarChart: FC<BarChartProps> = ({ config, data, analysis, onCellClick, allDimensions, lastConfigRef, compact = false }) => {
  const { x_axis_key, y_axis_keys, group_by_key, x_axis_date_format, y_axis_formatters = {} } = config;
  const barMode = (analysis?.viz_options?.bar_mode as string) || 'grouped';

  if (!x_axis_key || !y_axis_keys || y_axis_keys.length === 0) {
    return <div className="p-4 text-sm text-muted-foreground">Invalid bar chart configuration</div>;
  }

  // Get formatter for the first y-axis key (for y-axis label formatting)
  const firstYAxisFormatter = y_axis_formatters[y_axis_keys[0]] || 'NUMERIC';

  // Transform data for Recharts
  const transformedData: any[] = [];
  const xValuesSet = new Set<string>();
  const xValues: string[] = [];
  
  // Collect all unique x values in order (preserving backend sort order)
  data.forEach((row) => {
    if (row[x_axis_key] != null) {
      const rawValue = row[x_axis_key];
      const formattedValue = x_axis_date_format 
        ? formatDateValue(rawValue, x_axis_date_format)
        : String(rawValue);
      // Only add if we haven't seen this value before (preserve order)
      if (!xValuesSet.has(formattedValue)) {
        xValuesSet.add(formattedValue);
        xValues.push(formattedValue);
      }
    }
  });

  // Build chart config and data structure
  let chartConfig: ChartConfig = {};
  let dataKeys: string[] = [];

  if (group_by_key) {
    // Grouped bar chart
    const groups = new Set<string>();
    data.forEach((row) => {
      if (row[group_by_key] != null) {
        groups.add(String(row[group_by_key]));
      }
    });

    xValues.forEach((xVal) => {
      const entry: any = { [x_axis_key]: xVal };
      groups.forEach((group) => {
        // Find the row by matching the formatted x value
        const row = data.find((r) => {
          if (r[x_axis_key] == null) return false;
          const rawValue = r[x_axis_key];
          const formattedValue = x_axis_date_format 
            ? formatDateValue(rawValue, x_axis_date_format)
            : String(rawValue);
          return formattedValue === xVal && String(r[group_by_key]) === group;
        });
        y_axis_keys.forEach((yKey: string) => {
          const key = `${group}_${yKey}`;
          entry[key] = row?.[yKey] ?? 0;
          // Build chart config - store with both original and sanitized keys for lookup compatibility
          const sanitizedKey = sanitizeCssVariableName(key);
          if (!chartConfig[sanitizedKey]) {
            const colorIndex = (Array.from(groups).indexOf(group) * y_axis_keys.length + y_axis_keys.indexOf(yKey)) % CHART_COLORS.length;
            // If there's only one metric, just show the dimension value (group name)
            // Otherwise show "group - metric" format
            const label = y_axis_keys.length === 1 ? group : `${group} - ${yKey}`;
            const configEntry = {
              label: label,
              color: CHART_COLORS[colorIndex],
            };
            // Store with both keys so lookups work with either original or sanitized key
            chartConfig[sanitizedKey] = configEntry;
            if (sanitizedKey !== key) {
              chartConfig[key] = configEntry;
            }
          }
        });
      });
      transformedData.push(entry);
    });

    dataKeys = Array.from(groups).flatMap((group) =>
      y_axis_keys.map((yKey: string) => `${group}_${yKey}`)
    );
  } else {
    // Simple bar chart
    xValues.forEach((xVal) => {
      // Find the row by matching the formatted x value
      const row = data.find((r) => {
        if (r[x_axis_key] == null) return false;
        const rawValue = r[x_axis_key];
        const formattedValue = x_axis_date_format 
          ? formatDateValue(rawValue, x_axis_date_format)
          : String(rawValue);
        return formattedValue === xVal;
      });
      const entry: any = { [x_axis_key]: xVal };
      y_axis_keys.forEach((yKey: string) => {
        entry[yKey] = row?.[yKey] ?? 0;
        // Build chart config - store with both original and sanitized keys for lookup compatibility
        const sanitizedKey = sanitizeCssVariableName(yKey);
        if (!chartConfig[sanitizedKey]) {
          const colorIndex = y_axis_keys.indexOf(yKey) % CHART_COLORS.length;
          const configEntry = {
            label: yKey,
            color: CHART_COLORS[colorIndex],
          };
          // Store with both keys so lookups work with either original or sanitized key
          chartConfig[sanitizedKey] = configEntry;
          if (sanitizedKey !== yKey) {
            chartConfig[yKey] = configEntry;
          }
        }
      });
      transformedData.push(entry);
    });

    dataKeys = y_axis_keys;
  }

  // Calculate max value and tick interval for nice y-axis labels
  // Use the shared calculation function (bar charts always start at 0)
  const { maxValue, tickInterval } = useMemo(() => {
    if (barMode === 'stacked') {
      // For stacked charts, max Y = max sum of all series per x-value
      let stackedMax = 0;
      transformedData.forEach((entry) => {
        let sum = 0;
        dataKeys.forEach((key) => {
          const val = entry[key];
          if (val != null) {
            sum += typeof val === 'number' ? val : parseFloat(String(val)) || 0;
          }
        });
        if (sum > stackedMax) stackedMax = sum;
      });
      // Create a synthetic data array with the stacked max for bounds calculation
      const bounds = calculateAxisBounds([{ _stacked: stackedMax }], ['_stacked']);
      return {
        maxValue: bounds.maxValue,
        tickInterval: bounds.tickInterval ?? 20,
      };
    }
    const bounds = calculateAxisBounds(transformedData, dataKeys);
    // Bar charts always have non-negative values, so tickInterval should always be defined
    // But provide a fallback just in case
    return {
      maxValue: bounds.maxValue,
      tickInterval: bounds.tickInterval ?? 20,
    };
  }, [transformedData, dataKeys, barMode]);

  // Custom tooltip formatter
  const customTooltipFormatter = (value: any, name: any, item: any) => {
    // For grouped charts, extract the base metric key
    const nameStr = String(name || '');
    const baseKey = group_by_key 
      ? y_axis_keys.find((key: string) => nameStr.endsWith(`_${key}`) || nameStr === key) || y_axis_keys[0]
      : nameStr;
    const formatter = y_axis_formatters[baseKey] || 'NUMERIC';
    const numValue = typeof value === 'number' ? value : parseFloat(String(value)) || 0;
    const formattedValue = formatValue({ value: numValue, format: formatter });
    
    // For grouped charts, show category name with value
    if (group_by_key && chartConfig[nameStr]) {
      const categoryName = chartConfig[nameStr].label || nameStr;
      const indicatorColor = item.color || item.payload?.fill || '#000';
      return (
        <div className="flex w-full flex-wrap items-stretch gap-2 items-center">
          <div 
            className="shrink-0 rounded-[2px] border-[--color-border] bg-[--color-bg] h-2.5 w-2.5" 
            style={{ 
              '--color-bg': indicatorColor, 
              '--color-border': indicatorColor 
            } as CSSProperties} 
          />
          <div className="flex flex-1 justify-between leading-none items-center">
            <span className="text-muted-foreground mr-6">{categoryName}</span>
            <span className="font-mono font-medium tabular-nums text-foreground">{formattedValue}</span>
          </div>
        </div>
      );
    }
    
    // For any bar mode with multiple metrics, show metric name with value
    if (dataKeys.length > 1) {
      const metricLabel = chartConfig[sanitizeCssVariableName(nameStr)]?.label || nameStr;
      const indicatorColor = item.color || item.payload?.fill || '#000';
      return (
        <div className="flex w-full flex-wrap items-stretch gap-2 items-center">
          <div
            className="shrink-0 rounded-[2px] border-[--color-border] bg-[--color-bg] h-2.5 w-2.5"
            style={{
              '--color-bg': indicatorColor,
              '--color-border': indicatorColor
            } as CSSProperties}
          />
          <div className="flex flex-1 justify-between leading-none items-center">
            <span className="text-muted-foreground mr-6">{metricLabel}</span>
            <span className="font-mono font-medium tabular-nums text-foreground">{formattedValue}</span>
          </div>
        </div>
      );
    }

    // For non-grouped charts, just return the formatted value
    return formattedValue;
  };

  return (
    <ChartContainer config={chartConfig} className="h-full max-h-full w-full max-w-full aspect-auto">
      <RechartsBarChart
        data={transformedData}
        margin={compact ? { top: 24, right: 24, bottom: 24, left: 24 } : { top: (group_by_key || dataKeys.length > 1) ? 60 : 20, right: 130, bottom: 60, left: 60 }}
        accessibilityLayer
      >
        <CartesianGrid vertical={false} />
        {/* {!compact && ( */}
          <XAxis
            dataKey={x_axis_key}
            tickLine={true}
            tickMargin={10}
            axisLine={true}
            stroke="hsl(var(--border))"
            angle={-45}
            textAnchor="end"
            height={80}
          />
        {/* )} */}
        <YAxis
          tickLine={true}
          tickMargin={10}
          axisLine={true}
          stroke="hsl(var(--border))"
          domain={[0, maxValue]}
          interval={0}
          ticks={Array.from({ length: Math.floor(maxValue / tickInterval) + 1 }, (_, i) => i * tickInterval)}
          allowDecimals={false}
          tickFormatter={(value: number) => formatTickValue({ value, format: firstYAxisFormatter })}
          tick={true}
        />
        <ChartTooltip
          content={
            <ChartTooltipContent
              formatter={customTooltipFormatter}
            />
          }
        />
        {(group_by_key || dataKeys.length > 1) && (
          <ChartLegend
            content={<ChartLegendContent verticalAlign="top" />}
            verticalAlign="top"
            align="right"
            wrapperStyle={{ paddingBottom: '20px' }}
          />
        )}
        {dataKeys.map((key) => {
          // Handle bar click for drilldown
          const handleBarClick = (barData: any, index: number, e: any) => {
            if (!analysis) return;
            
            const xAxisValue = barData[x_axis_key];
            let originalRow: any = null;
            
            if (group_by_key) {
              // Grouped bar chart: Extract group name from key (e.g., "Kitchen_SALES" -> "Kitchen")
              // The key format is "${group}_${yKey}", so find which yKey it ends with
              let groupName = key;
              for (const yKey of y_axis_keys) {
                if (key.endsWith(`_${yKey}`)) {
                  groupName = key.slice(0, -(yKey.length + 1)); // Remove "_${yKey}" suffix
                  break;
                }
              }
              
              // Find the original data row that matches both x-axis value and group_by_key value
              originalRow = data.find((row: any) => {
                if (row[x_axis_key] == null) return false;
                const rawValue = row[x_axis_key];
                const formattedValue = x_axis_date_format 
                  ? formatDateValue(rawValue, x_axis_date_format)
                  : String(rawValue);
                return formattedValue === xAxisValue && String(row[group_by_key]) === groupName;
              });
            } else {
              // Simple bar chart: Find the row that matches the x-axis value
              originalRow = data.find((row: any) => {
                if (row[x_axis_key] == null) return false;
                const rawValue = row[x_axis_key];
                const formattedValue = x_axis_date_format 
                  ? formatDateValue(rawValue, x_axis_date_format)
                  : String(rawValue);
                return formattedValue === xAxisValue;
              });
            }
            
            if (originalRow && onCellClick) {
              const metricKey = group_by_key
                ? y_axis_keys[0]
                : key;

              // Defer the drilldown so Recharts finishes its internal click/hover
              // processing before we trigger a React state update (which would
              // confuse Recharts' tooltip positioning, causing it to jump to 0,0).
              setTimeout(() => {
                onCellClick(metricKey, originalRow[metricKey], originalRow, false);
              }, 0);
            }
          };
          
          const sanitizedKey = sanitizeCssVariableName(key);
          const color = chartConfig[sanitizedKey]?.color || CHART_COLORS[0];
          const isLastKey = key === dataKeys[dataKeys.length - 1];
          // For overlay mode, use a custom shape to render each bar from the baseline
          // even though they share a stackId (for positioning)
          const overlayShape = barMode === 'overlay' ? (props: any): React.ReactElement => {
            const { x, y, width, height, fill, index, value } = props;
            if (!height || height <= 0) return <g />;
            // value is [stackBottom, stackTop] in data units
            const stackBottom = value?.[0] ?? 0;
            const stackTop = value?.[1] ?? 0;
            const dataRange = stackTop - stackBottom;
            if (dataRange <= 0) return <g />;
            // Calculate baseline (y=0) in pixel coordinates
            const pixelsPerUnit = height / dataRange;
            const baseline = y + height + stackBottom * pixelsPerUnit;
            // Draw this bar from the baseline with height = its own value
            const barH = dataRange * pixelsPerUnit; // = height
            return (
              <rect
                x={x}
                y={baseline - barH}
                width={width}
                height={barH}
                fill={fill}
                rx={4}
                ry={4}
                style={{ cursor: 'pointer', pointerEvents: 'auto' }}
                onClick={(e) => {
                  e.stopPropagation();
                  const barData = transformedData[index];
                  if (barData) {
                    handleBarClick(barData, index, e);
                  }
                }}
              />
            );
          } : undefined;

          return (
            <Bar
              key={key}
              dataKey={key}
              fill={color}
              radius={barMode === 'stacked' ? (isLastKey ? [4, 4, 0, 0] : 0) : 4}
              onClick={handleBarClick}
              style={{ cursor: 'pointer' }}
              {...(barMode === 'stacked' || barMode === 'overlay' ? { stackId: 'stack' } : {})}
              {...(barMode === 'overlay' ? { shape: overlayShape } : {})}
            />
          );
        })}
      </RechartsBarChart>
    </ChartContainer>
  );
};

interface LineChartProps {
  config: any;
  data: Record<string, any>[];
  compact?: boolean;
}

const LineChart: FC<LineChartProps> = ({ config, data, compact = false }) => {
  const { x_axis_key, y_axis_keys, group_by_key, x_axis_date_format, y_axis_formatters = {} } = config;

  if (!x_axis_key || !y_axis_keys || y_axis_keys.length === 0) {
    return <div className="p-4 text-sm text-muted-foreground">Invalid line chart configuration</div>;
  }

  // Get formatter for the first y-axis key (for y-axis label formatting)
  const firstYAxisFormatter = y_axis_formatters[y_axis_keys[0]] || 'NUMERIC';

  // Transform data for Recharts
  const xValuesSet = new Set<string>();
  const xValues: string[] = [];
  
  // Collect all unique x values in order (preserving backend sort order)
  data.forEach((row) => {
    if (row[x_axis_key] != null) {
      const rawValue = row[x_axis_key];
      const formattedValue = x_axis_date_format 
        ? formatDateValue(rawValue, x_axis_date_format)
        : String(rawValue);
      // Only add if we haven't seen this value before (preserve order)
      if (!xValuesSet.has(formattedValue)) {
        xValuesSet.add(formattedValue);
        xValues.push(formattedValue);
      }
    }
  });

  // Build chart config and data structure
  let chartConfig: ChartConfig = {};
  let transformedData: any[] = [];
  let dataKeys: string[] = [];

  if (group_by_key) {
    // Grouped line chart
    const groups = new Set<string>();
    data.forEach((row) => {
      if (row[group_by_key] != null) {
        groups.add(String(row[group_by_key]));
      }
    });

    // Transform to flat array format for Recharts
    xValues.forEach((xVal) => {
      const entry: any = { [x_axis_key]: xVal };
      groups.forEach((group) => {
        // Find the row by matching the formatted x value
        const row = data.find((r) => {
          if (r[x_axis_key] == null) return false;
          const rawValue = r[x_axis_key];
          const formattedValue = x_axis_date_format 
            ? formatDateValue(rawValue, x_axis_date_format)
            : String(rawValue);
          return formattedValue === xVal && String(r[group_by_key]) === group;
        });
        y_axis_keys.forEach((yKey: string) => {
          const key = `${group}_${yKey}`;
          entry[key] = row?.[yKey] ?? 0;
          // Build chart config - store with both original and sanitized keys for lookup compatibility
          const sanitizedKey = sanitizeCssVariableName(key);
          if (!chartConfig[sanitizedKey]) {
            const colorIndex = (Array.from(groups).indexOf(group) * y_axis_keys.length + y_axis_keys.indexOf(yKey)) % CHART_COLORS.length;
            // If there's only one metric, just show the dimension value (group name)
            // Otherwise show "group - metric" format
            const label = y_axis_keys.length === 1 ? group : `${group} - ${yKey}`;
            const configEntry = {
              label: label,
              color: CHART_COLORS[colorIndex],
            };
            // Store with both keys so lookups work with either original or sanitized key
            chartConfig[sanitizedKey] = configEntry;
            if (sanitizedKey !== key) {
              chartConfig[key] = configEntry;
            }
          }
        });
      });
      transformedData.push(entry);
    });

    dataKeys = Array.from(groups).flatMap((group) =>
      y_axis_keys.map((yKey: string) => `${group}_${yKey}`)
    );
  } else {
    // Simple line chart
    xValues.forEach((xVal) => {
      // Find the row by matching the formatted x value
      const row = data.find((r) => {
        if (r[x_axis_key] == null) return false;
        const rawValue = r[x_axis_key];
        const formattedValue = x_axis_date_format 
          ? formatDateValue(rawValue, x_axis_date_format)
          : String(rawValue);
        return formattedValue === xVal;
      });
      const entry: any = { [x_axis_key]: xVal };
      y_axis_keys.forEach((yKey: string) => {
        entry[yKey] = row?.[yKey] ?? 0;
        // Build chart config - store with both original and sanitized keys for lookup compatibility
        const sanitizedKey = sanitizeCssVariableName(yKey);
        if (!chartConfig[sanitizedKey]) {
          const colorIndex = y_axis_keys.indexOf(yKey) % CHART_COLORS.length;
          const configEntry = {
            label: yKey,
            color: CHART_COLORS[colorIndex],
          };
          // Store with both keys so lookups work with either original or sanitized key
          chartConfig[sanitizedKey] = configEntry;
          if (sanitizedKey !== yKey) {
            chartConfig[yKey] = configEntry;
          }
        }
      });
      transformedData.push(entry);
    });

    dataKeys = y_axis_keys;
  }

  // Calculate min and max values for y-axis domain
  // Use the shared calculation function
  const { minValue, maxValue, tickInterval } = useMemo(() => {
    return calculateAxisBounds(transformedData, dataKeys);
  }, [transformedData, dataKeys]);

  // Custom tooltip formatter
  const customTooltipFormatter = (value: any, name: any, item: any) => {
    // For grouped charts, extract the base metric key
    const nameStr = String(name || '');
    const baseKey = group_by_key 
      ? y_axis_keys.find((key: string) => nameStr.endsWith(`_${key}`) || nameStr === key) || y_axis_keys[0]
      : nameStr;
    const formatter = y_axis_formatters[baseKey] || 'NUMERIC';
    const numValue = typeof value === 'number' ? value : parseFloat(String(value)) || 0;
    const formattedValue = formatValue({ value: numValue, format: formatter });
    
    // For grouped charts, show category name with value
    if (group_by_key && chartConfig[nameStr]) {
      const categoryName = chartConfig[nameStr].label || nameStr;
      const indicatorColor = item.color || item.payload?.fill || '#000';
      return (
        <div className="flex w-full flex-wrap items-stretch gap-2 items-center">
          <div 
            className="shrink-0 rounded-[2px] border-[--color-border] bg-[--color-bg] h-2.5 w-2.5" 
            style={{ 
              '--color-bg': indicatorColor, 
              '--color-border': indicatorColor 
            } as CSSProperties} 
          />
          <div className="flex flex-1 justify-between leading-none items-center">
            <span className="text-muted-foreground mr-6">{categoryName}</span>
            <span className="font-mono font-medium tabular-nums text-foreground">{formattedValue}</span>
          </div>
        </div>
      );
    }
    
    // For non-grouped charts, just return the formatted value
    return formattedValue;
  };

  return (
    <ChartContainer config={chartConfig} className="h-full max-h-full w-full max-w-full aspect-auto">
      <RechartsLineChart
        data={transformedData}
        margin={compact ? { top: 24, right: 24, bottom: 24, left: 24 } : { top: group_by_key ? 60 : 20, right: 130, bottom: 60, left: 60 }}
        accessibilityLayer
      >
        <CartesianGrid vertical={false} />
        <XAxis
            dataKey={x_axis_key}
            tickLine={false}
            tickMargin={10}
            axisLine={true}
            stroke="hsl(var(--border))"
            angle={-45}
            textAnchor="end"
            height={80}
          />
        <YAxis
          tickLine={true}
          tickMargin={10}
          axisLine={true}
          stroke="hsl(var(--border))"
          domain={[minValue, maxValue]}
          interval={tickInterval ? 0 : undefined}
          ticks={tickInterval ? (() => {
            // Calculate ticks that align with the domain [minValue, maxValue]
            // Start from the first tick >= minValue, go up to maxValue
            const firstTick = Math.ceil(minValue / tickInterval) * tickInterval;
            const tickCount = Math.floor((maxValue - firstTick) / tickInterval) + 1;
            return Array.from({ length: tickCount }, (_, i) => firstTick + i * tickInterval);
          })() : undefined}
          allowDecimals={tickInterval ? false : undefined}
          tickFormatter={(value: number) => formatTickValue({ value, format: firstYAxisFormatter })}
          tick={true}
        />
        <ChartTooltip
          content={
            <ChartTooltipContent
              formatter={customTooltipFormatter}
            />
          }
        />
        {group_by_key && !compact && (
          <ChartLegend
            content={<ChartLegendContent verticalAlign="top" />}
            verticalAlign="top"
            align="right"
            wrapperStyle={{ paddingBottom: '20px' }}
          />
        )}
        {dataKeys.map((key) => {
          const sanitizedKey = sanitizeCssVariableName(key);
          const color = chartConfig[sanitizedKey]?.color || CHART_COLORS[0];
          return (
            <Line
              key={key}
              type="monotone"
              dataKey={key}
              stroke={color}
              strokeWidth={2}
              dot={{ r: 4 }}
              activeDot={{ r: 6 }}
            />
          );
        })}
      </RechartsLineChart>
    </ChartContainer>
  );
};

interface PieChartProps {
  config: any;
  data: Record<string, any>[];
  analysis?: Analysis | null;
  onCellClick?: (key: string, value: any, row: Record<string, any>, isDimension: boolean) => void;
  compact?: boolean;
}

export const PieChart: FC<PieChartProps> = ({ config, data, analysis, onCellClick, compact = false }) => {
  const { dimension_key, metric_key, formatter = 'NUMERIC' } = config;
  const containerRef = useRef<HTMLDivElement>(null);
  const [outerRadius, setOuterRadius] = useState(compact ? 80 : 120);

  if (!dimension_key || !metric_key) {
    return <div className="p-4 text-sm text-muted-foreground">Invalid pie chart configuration</div>;
  }

  // Calculate radius based on container size
  useEffect(() => {
    const updateRadius = () => {
      if (containerRef.current) {
        const { width, height } = containerRef.current.getBoundingClientRect();
        // Use the smaller dimension to ensure the pie fits, leave some padding for legend
        // Leave space for legend at bottom (about 20% of height)
        const availableHeight = height * 0.8;
        const minDimension = Math.min(width, availableHeight);
        // Calculate radius as 40% of the smaller dimension
        const calculatedRadius = minDimension * 0.4;
        setOuterRadius(Math.max(calculatedRadius, compact ? 60 : 80)); // Min radius only
      }
    };

    updateRadius();
    const resizeObserver = new ResizeObserver(updateRadius);
    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }

    // Also listen to window resize as a fallback
    window.addEventListener('resize', updateRadius);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener('resize', updateRadius);
    };
  }, [compact]);

  // Transform data for Recharts - each row becomes a pie slice
  const pieData = data.map((row, index) => {
    const name = row[dimension_key] != null ? String(row[dimension_key]) : 'Unknown';
    const value = typeof row[metric_key] === 'number' ? row[metric_key] : parseFloat(String(row[metric_key])) || 0;
    return {
      name,
      value,
      originalRow: row, // Keep reference to original row for click handling
      index,
    };
  }).filter(item => item.value > 0); // Filter out zero values

  // Build chart config for colors
  const chartConfig = buildPieChartConfig(pieData.map(item => item.name));

  // Custom tooltip formatter
  const customTooltipFormatter = (value: any, name: any, item: any) => {
    const numValue = typeof value === 'number' ? value : parseFloat(String(value)) || 0;
    const formattedValue = formatValue({ value: numValue, format: formatter });
    
    // Calculate percentage
    const total = pieData.reduce((sum, d) => sum + d.value, 0);
    const percentage = total > 0 ? ((numValue / total) * 100).toFixed(1) : '0.0';
    
    return (
      <div className="flex w-full flex-wrap items-stretch gap-2 items-center">
        <div 
          className="shrink-0 rounded-[2px] border-[--color-border] bg-[--color-bg] h-2.5 w-2.5" 
          style={{ 
            '--color-bg': item.payload?.fill || CHART_COLORS[0], 
            '--color-border': item.payload?.fill || CHART_COLORS[0] 
          } as CSSProperties} 
        />
        <div className="flex flex-1 flex-col gap-1 leading-none">
          <span className="text-muted-foreground">{name}</span>
          <div className="flex items-center gap-2">
            <span className="font-mono font-medium tabular-nums text-foreground">{formattedValue}</span>
            <span className="text-muted-foreground text-xs">({percentage}%)</span>
          </div>
        </div>
      </div>
    );
  };

  // Handle pie slice click
  const handlePieClick = (data: any, index: number, e: any) => {
    if (!analysis || !onCellClick) return;
    
    const clickedItem = pieData[index];
    if (clickedItem && clickedItem.originalRow) {
      // For pie charts, clicking a slice should trigger metric drilldown
      // Pass the metric key and the metric value (slice value) for drilldown
      // The dimension filter for the clicked slice will be automatically included via rowDimensions
      onCellClick(metric_key, clickedItem.value, clickedItem.originalRow, false);
    }
  };

  return (
    <div ref={containerRef} className="h-full w-full">
      <ChartContainer 
        config={chartConfig} 
        className="h-full max-h-full w-full max-w-full aspect-auto"
      >
        <RechartsPieChart>
          <Pie
            data={pieData}
            cx="50%"
            cy="50%"
            labelLine={false}
            label={({ name, percent }) => {
              // Only show label if percentage is significant (> 2%)
              if (percent < 0.02) return '';
              return `${(percent * 100).toFixed(0)}%`;
            }}
            outerRadius={outerRadius}
            fill="#8884d8"
            dataKey="value"
            onClick={handlePieClick}
            style={{ cursor: onCellClick ? 'pointer' : 'default' }}
          >
            {pieData.map((entry, index) => {
              const color = chartConfig[entry.name]?.color || CHART_COLORS[index % CHART_COLORS.length];
              return <Cell key={`cell-${index}`} fill={color} />;
            })}
          </Pie>
          <ChartTooltip
            content={
              <ChartTooltipContent
                formatter={customTooltipFormatter}
              />
            }
          />
          <ChartLegend
            content={<ChartLegendContent nameKey="name" />}
            verticalAlign="bottom"
            align="center"
          />
        </RechartsPieChart>
      </ChartContainer>
    </div>
  );
};

interface SingleValueChartProps {
  config: any;
  data: Record<string, any>[];
  analysis?: Analysis;
}

const SingleValueChart: FC<SingleValueChartProps> = ({ config, data, analysis }) => {
  const { y_axis_key, y_axis_formatters = {}, comparison_key, comparison_label } = config;
  const value = data[0]?.[y_axis_key];
  const comparisonValue = comparison_key ? data[0]?.[comparison_key] : undefined;
  const formatOverride = analysis?.viz_options?.format_override as string | undefined;
  const formatter = formatOverride || y_axis_formatters[y_axis_key] || 'NUMERIC';

  const containerRef = useRef<HTMLDivElement>(null);
  const textRef = useRef<HTMLDivElement>(null);
  const [fontSize, setFontSize] = useState(72);

  const formatSingleValue = useCallback((val: any) => {
    if (val == null) return 'N/A';
    return formatValue({ value: Number(val), format: formatter });
  }, [formatter]);

  const displayValue = useMemo(() => formatSingleValue(value), [value, formatSingleValue]);

  const comparisonDisplay = useMemo(() => {
    if (comparisonValue == null || value == null) return null;
    const numValue = Number(value);
    const numComparison = Number(comparisonValue);
    if (numComparison === 0) {
      return {
        formattedValue: formatSingleValue(comparisonValue),
        pctChange: null,
        isPositive: false,
        isNeutral: true,
        noData: true,
      };
    }
    const pctChange = ((numValue - numComparison) / Math.abs(numComparison)) * 100;
    const isPositive = pctChange > 0;
    const isNeutral = pctChange === 0;
    return {
      formattedValue: formatSingleValue(comparisonValue),
      pctChange: isNeutral ? '0%' : `${isPositive ? '+' : ''}${pctChange.toFixed(1)}%`,
      isPositive,
      isNeutral,
      noData: false,
    };
  }, [value, comparisonValue, formatSingleValue]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const fit = () => {
      const text = textRef.current;
      if (!text || !container) return;

      const padding = 32; // match p-4 on each side
      const containerWidth = container.clientWidth - padding * 2;
      const containerHeight = container.clientHeight - padding * 2;
      if (containerWidth === 0 || containerHeight === 0) return;

      let size = 72;
      const minSize = 14;
      text.style.fontSize = size + 'px';

      while (size > minSize && (text.scrollWidth > containerWidth || text.scrollHeight > containerHeight)) {
        size -= 2;
        text.style.fontSize = size + 'px';
      }

      setFontSize(size);
    };

    const observer = new ResizeObserver(fit);
    observer.observe(container);
    fit();

    return () => observer.disconnect();
  }, [displayValue, comparisonDisplay]);

  return (
    <div ref={containerRef} className="p-4 h-full w-full flex flex-col items-center justify-center overflow-hidden">
      <div
        ref={textRef}
        className="font-bold text-foreground whitespace-nowrap"
        style={{ fontSize: `${fontSize}px`, lineHeight: 1.1 }}
      >
        {displayValue}
      </div>
      <div className="mt-2 text-xs" style={{ visibility: comparisonDisplay ? 'visible' : 'hidden' }}>
        {comparisonDisplay ? (
          <div className="flex items-center gap-1 justify-center">
            <span className={`flex items-center gap-0.5 font-medium whitespace-nowrap ${
              comparisonDisplay.isNeutral
                ? 'text-muted-foreground'
                : comparisonDisplay.isPositive
                  ? 'text-green-600 dark:text-green-400'
                  : 'text-red-600 dark:text-red-400'
            }`}>
              {!comparisonDisplay.isNeutral && (
                comparisonDisplay.isPositive
                  ? <ArrowUp className="h-3 w-3" />
                  : <ArrowDown className="h-3 w-3" />
              )}
              {comparisonDisplay.pctChange}
            </span>
            <span className="text-muted-foreground whitespace-nowrap">
              vs {comparisonDisplay.formattedValue}
            </span>
          </div>
        ) : (
          <span>&nbsp;</span>
        )}
      </div>
    </div>
  );
};

interface TableChartProps {
  config: any;
  data: Record<string, any>[];
  sortState?: SortState;
  onSortToggle?: (columnName: string) => void;
  analysis?: Analysis | null;
  onCellClick?: (key: string, value: any, row: Record<string, any>, isDimension: boolean) => void;
  compact?: boolean;
}

const TABLE_PAGE_SIZE = 25;

const TableChart: FC<TableChartProps> = ({ config, data, sortState = {}, onSortToggle, analysis, onCellClick, compact = false }) => {
  const [page, setPage] = useState(1);
  const { dimension_keys = [], metric_keys = [], y_axis_formatters = {}, dimension_date_formats = {} } = config;
  const allKeys = [...dimension_keys, ...metric_keys];

  const totalRecords = data.length;
  const totalPages = Math.max(1, Math.ceil(totalRecords / TABLE_PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pageData = data.slice((currentPage - 1) * TABLE_PAGE_SIZE, currentPage * TABLE_PAGE_SIZE);

  // Reset to page 1 when data changes (e.g. sort)
  useEffect(() => { setPage(1); }, [totalRecords]);

  if (data.length === 0) {
    return (
      <div className={compact ? 'p-4' : ''}>
        <div className="p-4 bg-muted/50 border border-border rounded">
          <p className="text-muted-foreground text-sm">No data available for the provided date range &amp; filters</p>
        </div>
      </div>
    );
  }

  // Helper function to format cell values
  const formatCellValue = (key: string, value: any): string => {
    // If it's a metric, use the formatter
    if (metric_keys.includes(key)) {
      const formatter = y_axis_formatters[key] || 'NUMERIC';
      const numValue = typeof value === 'number' ? value : parseFloat(String(value));
      if (value == null || isNaN(numValue)) {
        return '-';
      }
      return formatValue({ value: numValue, format: formatter });
    }
    // For dimensions, format dates if a date format is available
    if (dimension_date_formats[key]) {
      return formatDateValue(value, dimension_date_formats[key]);
    }
    return value != null ? String(value) : '';
  };

  const getSortIcon = (columnName: string) => {
    const direction = sortState[columnName];
    if (direction === 'asc') {
      return <ArrowUp className="h-4 w-4" />;
    } else if (direction === 'desc') {
      return <ArrowDown className="h-4 w-4" />;
    }
    return <ArrowUpDown className="h-4 w-4 text-muted-foreground" />;
  };

  return (
    <div className="w-full min-w-0 max-w-full h-full flex flex-col">
      <div className="flex-1 min-h-0 overflow-auto">
        <Table className="w-full">
          <TableHeader className="sticky top-0 z-10 bg-background border-b shadow-sm">
            <TableRow>
              {allKeys.map((key) => (
                <TableHead key={key} className="bg-background">
                  {onSortToggle ? (
                    <button
                      onClick={() => onSortToggle(key)}
                      className="flex items-center gap-2 hover:opacity-70 transition-opacity"
                      title="Click to sort"
                    >
                      <span>{key}</span>
                      {getSortIcon(key)}
                    </button>
                  ) : (
                    <div className="flex items-center gap-2">
                      <span>{key}</span>
                    </div>
                  )}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {pageData.map((row, idx) => (
              <TableRow key={idx}>
                {allKeys.map((key) => {
                  const isDimension = dimension_keys.includes(key);
                  const cellValue = formatCellValue(key, row[key]);
                  const isClickable = onCellClick && cellValue !== '-';

                  return (
                    <TableCell
                      key={key}
                      className={isClickable ? 'cursor-pointer hover:bg-muted/50 transition-colors' : ''}
                      onClick={() => {
                        if (isClickable) {
                          onCellClick?.(key, row[key], row, isDimension);
                        }
                      }}
                    >
                      {cellValue}
                    </TableCell>
                  );
                })}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      {totalPages > 1 && (
        <div className={`mt-2 flex items-center justify-between flex-shrink-0 ${compact ? 'px-2 pb-2' : ''}`}>
          <div className="text-sm text-muted-foreground">
            Showing {pageData.length} of {summarizeNumber(totalRecords)} records
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={currentPage === 1}
            >
              <ChevronLeft className="h-4 w-4" />
              Previous
            </Button>
            <div className="text-sm text-muted-foreground">
              Page {currentPage} of {summarizeNumber(totalPages)}
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={currentPage >= totalPages}
            >
              Next
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

// ===== PIVOT TABLE CHART =====

interface PivotTableChartProps {
  config: any;
  data: any[];
  analysis?: Analysis | null;
  onCellClick?: (key: string, value: any, row: Record<string, any>, isDimension: boolean) => void;
}

export const PivotTableChart: FC<PivotTableChartProps> = ({ config, data, analysis, onCellClick }) => {
  const [page, setPage] = useState(1);
  const { dimension_keys = [], metric_keys = [], y_axis_formatters = {}, dimension_date_formats = {} } = config;
  const columnDimKey = dimension_keys[0];
  const rowDimKeys: string[] = dimension_keys.slice(1);
  const metricKey = metric_keys[0];

  const { columnValues, pivotMap } = useMemo(() => {
    // Collect unique column values
    const colValuesSet = new Set<string>();
    data.forEach(row => {
      const v = row[columnDimKey];
      if (v != null) colValuesSet.add(String(v));
    });
    const columnValues = Array.from(colValuesSet).sort();

    // Build pivot map: composite row key -> { rowDimValues, metrics by column value }
    const pivotMap = new Map<string, { rowDimValues: Record<string, any>; metrics: Map<string, any> }>();

    data.forEach(row => {
      const compositeKey = rowDimKeys.map(k => String(row[k] ?? '')).join('\x00');
      if (!pivotMap.has(compositeKey)) {
        const rowDimValues: Record<string, any> = {};
        rowDimKeys.forEach(k => { rowDimValues[k] = row[k]; });
        pivotMap.set(compositeKey, { rowDimValues, metrics: new Map() });
      }
      const entry = pivotMap.get(compositeKey)!;
      const colVal = String(row[columnDimKey] ?? '');
      entry.metrics.set(colVal, row[metricKey]);
    });

    return { columnValues, pivotMap };
  }, [data, columnDimKey, rowDimKeys, metricKey]);

  const rows = Array.from(pivotMap.entries());
  const totalRows = rows.length;
  const totalPages = Math.max(1, Math.ceil(totalRows / TABLE_PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pageRows = rows.slice((currentPage - 1) * TABLE_PAGE_SIZE, currentPage * TABLE_PAGE_SIZE);

  // Reset to page 1 when data changes
  useEffect(() => { setPage(1); }, [totalRows]);

  if (data.length === 0 || !columnDimKey || rowDimKeys.length === 0 || !metricKey) {
    return (
      <div className="p-4 bg-muted/50 border border-border rounded">
        <p className="text-muted-foreground text-sm">
          {data.length === 0
            ? 'No data available for the provided date range & filters'
            : 'Pivot table requires at least 1 column dimension, 1 row dimension, and 1 metric'}
        </p>
      </div>
    );
  }

  const formatter = y_axis_formatters[metricKey] || 'NUMERIC';

  const formatMetric = (value: any): string => {
    if (value == null) return '-';
    const numValue = typeof value === 'number' ? value : parseFloat(String(value));
    if (isNaN(numValue)) return '-';
    return formatValue({ value: numValue, format: formatter });
  };

  return (
    <div className="w-full min-w-0 max-w-full h-full flex flex-col">
      <div className="flex-1 min-h-0 overflow-auto">
        <Table className="w-full">
          <TableHeader className="sticky top-0 z-10 bg-background border-b shadow-sm">
            <TableRow>
              {rowDimKeys.map(key => (
                <TableHead key={key} className="bg-background font-bold">
                  {key}
                </TableHead>
              ))}
              {columnValues.map(colVal => (
                <TableHead key={colVal} className="bg-background text-right font-bold">
                  {dimension_date_formats[columnDimKey] ? formatDateValue(colVal, dimension_date_formats[columnDimKey]) : colVal}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {pageRows.map(([compositeKey, entry], idx) => (
              <TableRow key={compositeKey || idx}>
                {rowDimKeys.map(dimKey => {
                  const value = entry.rowDimValues[dimKey];
                  const isClickable = onCellClick && value != null;
                  // Build a synthetic row with all dimension values for drilldown
                  const syntheticRow: Record<string, any> = { ...entry.rowDimValues };
                  return (
                    <TableHead
                      key={dimKey}
                      className={`font-semibold ${isClickable ? 'cursor-pointer hover:bg-muted/50 transition-colors' : ''}`}
                      onClick={() => {
                        if (isClickable) {
                          onCellClick?.(dimKey, value, syntheticRow, true);
                        }
                      }}
                    >
                      {value != null ? (dimension_date_formats[dimKey] ? formatDateValue(value, dimension_date_formats[dimKey]) : String(value)) : ''}
                    </TableHead>
                  );
                })}
                {columnValues.map(colVal => {
                  const metricValue = entry.metrics.get(colVal);
                  const formatted = formatMetric(metricValue);
                  const isClickable = onCellClick && formatted !== '-';
                  // Synthetic row includes row dims + column dim value for full context
                  const syntheticRow: Record<string, any> = {
                    ...entry.rowDimValues,
                    [columnDimKey]: colVal,
                    [metricKey]: metricValue,
                  };
                  return (
                    <TableCell
                      key={colVal}
                      className={`text-right tabular-nums ${isClickable ? 'cursor-pointer hover:bg-muted/50 transition-colors' : ''}`}
                      onClick={() => {
                        if (isClickable) {
                          onCellClick?.(metricKey, metricValue, syntheticRow, false);
                        }
                      }}
                    >
                      {formatted}
                    </TableCell>
                  );
                })}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      {totalPages > 1 && (
        <div className="mt-2 flex items-center justify-between flex-shrink-0">
          <div className="text-sm text-muted-foreground">
            Showing {pageRows.length} of {summarizeNumber(totalRows)} rows
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={currentPage === 1}
            >
              <ChevronLeft className="h-4 w-4" />
              Previous
            </Button>
            <div className="text-sm text-muted-foreground">
              Page {currentPage} of {summarizeNumber(totalPages)}
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={currentPage >= totalPages}
            >
              Next
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

// ===== LTV PIVOT TABLE CHART =====

interface LTVVisualizationRow {
  ACQUISITION_COHORT: string;
  PERIODS_SINCE_FIRST_PURCHASE: number;
  AVERAGE_TOTAL_SALES: string;
  CUMULATIVE_VALUE: number;
  NUMBER_OF_CUSTOMERS: number;
  NUMBER_OF_CUSTOMERS_IN_COHORT: number;
}

interface LTVRepurchaseRow {
  ACQUISITION_COHORT: string;
  NUMBER_OF_CUSTOMERS: number;
  NUMBER_OF_REPURCHASERS: number;
}

interface LTVAdSpendRow {
  ACQUISITION_COHORT: string;
  AD_SPEND: number;
}

interface LTVPivotTableChartProps {
  config: any;
  analysis?: Analysis | null;
}

// Generate color for heatmap cells based on value range
function generateHeatmapColor(min: number, max: number, value: number): string {
  if (min === max) {
    return 'rgb(51, 102, 204)'; // Medium blue
  }
  const range = max - min;
  const percentage = (value - min) / range;

  // Start with a medium blue and go to dark blue
  const red = Math.floor(51 - (51 * percentage));
  const green = Math.floor(102 - (51 * percentage));
  const blue = Math.floor(204 - (51 * percentage));

  return `rgb(${red}, ${green}, ${blue})`;
}

const LTVPivotTableChart: FC<LTVPivotTableChartProps> = ({ config, analysis }) => {
  const [cumulative, setCumulative] = useState(true);
  const [showCacPayback, setShowCacPayback] = useState(false);
  const [metricToUse, setMetricToUse] = useState<'AVERAGE_TOTAL_SALES' | 'NUMBER_OF_CUSTOMERS'>('AVERAGE_TOTAL_SALES');

  // Extract datasets from config.options
  const dataset1: LTVVisualizationRow[] = config?.options?.dataset1 || [];
  const dataset2: LTVRepurchaseRow[] = config?.options?.dataset2 || [];
  const dataset3: LTVAdSpendRow[] | undefined = config?.options?.dataset3;
  const dateFormat: string | undefined = config?.options?.date_format;

  // Get unique period headers
  const periodHeaders = useMemo(() => {
    if (!dataset1 || dataset1.length === 0) {
      return [];
    }
    const periods = new Set(dataset1.map(d => d.PERIODS_SINCE_FIRST_PURCHASE));
    return Array.from(periods).sort((a, b) => a - b);
  }, [dataset1]);

  // Group rows by cohort and calculate cumulative values
  const rows = useMemo(() => {
    const rv: LTVVisualizationRow[][] = [];
    if (!dataset1 || dataset1.length === 0) {
      return [];
    }

    dataset1.forEach(row => {
      if (!row || !row.ACQUISITION_COHORT) return;
      const idx = rv.findIndex(r => r[0].ACQUISITION_COHORT === row.ACQUISITION_COHORT);
      if (idx === -1) {
        rv.push([row]);
      } else {
        rv[idx].push(row);
      }
    });

    // Sort each row by period
    rv.forEach(row => {
      row.sort((a, b) => a.PERIODS_SINCE_FIRST_PURCHASE - b.PERIODS_SINCE_FIRST_PURCHASE);
    });

    // Sort rows by acquisition cohort
    rv.sort((a, b) => a[0].ACQUISITION_COHORT > b[0].ACQUISITION_COHORT ? 1 : -1);

    // Calculate cumulative values
    rv.forEach(row => {
      const firstSales = row[0].AVERAGE_TOTAL_SALES;
      row[0].CUMULATIVE_VALUE = firstSales ? parseFloat(firstSales) : 0;

      for (let i = 1; i < row.length; i++) {
        const currentSales = row[i].AVERAGE_TOTAL_SALES;
        const currentValue = currentSales ? parseFloat(currentSales) : 0;
        const previousCumulative = row[i - 1].CUMULATIVE_VALUE || 0;
        row[i].CUMULATIVE_VALUE = currentValue + previousCumulative;
      }
    });

    return rv;
  }, [dataset1]);

  // Calculate min/max values per period for heatmap coloring
  const allValsByPeriod = useMemo(() => {
    const rv: { [key: number]: number[] } = {};
    rows.forEach(row => {
      if (!Array.isArray(row)) return;
      row.forEach(c => {
        if (c.PERIODS_SINCE_FIRST_PURCHASE == null) return;

        if (!rv[c.PERIODS_SINCE_FIRST_PURCHASE]) {
          rv[c.PERIODS_SINCE_FIRST_PURCHASE] = [];
        }
        if (metricToUse === 'NUMBER_OF_CUSTOMERS') {
          if (c.NUMBER_OF_CUSTOMERS != null) {
            rv[c.PERIODS_SINCE_FIRST_PURCHASE].push(c.NUMBER_OF_CUSTOMERS);
          }
        } else if (metricToUse === 'AVERAGE_TOTAL_SALES') {
          if (cumulative) {
            if (c.CUMULATIVE_VALUE != null) {
              rv[c.PERIODS_SINCE_FIRST_PURCHASE].push(c.CUMULATIVE_VALUE);
            }
          } else {
            if (c.AVERAGE_TOTAL_SALES != null) {
              rv[c.PERIODS_SINCE_FIRST_PURCHASE].push(parseFloat(c.AVERAGE_TOTAL_SALES));
            }
          }
        }
      });
    });
    return rv;
  }, [rows, metricToUse, cumulative]);

  // Calculate global min/max across ALL cells for consistent heatmap coloring
  const { globalMin, globalMax } = useMemo(() => {
    const allValues: number[] = [];
    Object.values(allValsByPeriod).forEach(values => {
      if (values && Array.isArray(values)) {
        allValues.push(...values.filter(v => v != null && !isNaN(v)));
      }
    });
    if (allValues.length === 0) {
      return { globalMin: 0, globalMax: 0 };
    }
    return {
      globalMin: Math.min(...allValues),
      globalMax: Math.max(...allValues),
    };
  }, [allValsByPeriod]);

  // Calculate repurchase rate by cohort
  const repurchaseRateByCohort = useMemo(() => {
    if (!dataset2 || dataset2.length === 0) {
      return {};
    }
    const rv: { [key: string]: number } = {};
    dataset2.forEach(row => {
      rv[row.ACQUISITION_COHORT] = row.NUMBER_OF_REPURCHASERS / row.NUMBER_OF_CUSTOMERS;
    });
    return rv;
  }, [dataset2]);

  // Calculate CAC by cohort
  const cacByCohort = useMemo(() => {
    if (!dataset3 || dataset3.length === 0) {
      return {};
    }
    if (!analysis?.measure_ids || analysis.measure_ids.length < 2) {
      return {};
    }

    const newCustomersByCohort: { [key: string]: number } = {};
    rows.forEach(row => {
      if (row[0]) {
        newCustomersByCohort[row[0].ACQUISITION_COHORT] = row[0].NUMBER_OF_CUSTOMERS_IN_COHORT;
      }
    });

    const rv: { [key: string]: number } = {};
    dataset3.forEach(row => {
      rv[row.ACQUISITION_COHORT] = row.AD_SPEND / newCustomersByCohort[row.ACQUISITION_COHORT];
    });
    return rv;
  }, [dataset3, analysis, rows]);

  const includeCAC = useMemo(() => {
    return analysis?.measure_ids && analysis.measure_ids.length > 1;
  }, [analysis?.measure_ids]);

  if (rows.length === 0) {
    return (
      <div className="p-4 bg-muted/50 border border-border rounded">
        <p className="text-muted-foreground text-sm">No data available for the provided date range &amp; filters</p>
      </div>
    );
  }

  return (
    <div className="h-full w-full overflow-auto">
      {/* Controls */}
      <div className="flex items-center justify-between p-3 mb-2 border border-border rounded-md bg-muted/30">
        <div className="font-medium text-sm">
          {metricToUse === 'NUMBER_OF_CUSTOMERS' && (
            <span>Showing: # of customers who purchased in each period</span>
          )}
          {metricToUse === 'AVERAGE_TOTAL_SALES' && (
            <span>Showing: {cumulative ? 'Cumulative' : 'Average'} LTV by period</span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {includeCAC && (
            <button
              className={`px-3 py-1.5 text-sm rounded-md border flex items-center gap-1.5 ${showCacPayback ? 'bg-primary text-primary-foreground border-primary' : 'bg-background hover:bg-muted border-border'}`}
              onClick={() => setShowCacPayback(!showCacPayback)}
            >
              {showCacPayback ? <Eye className="h-3.5 w-3.5" /> : <EyeOff className="h-3.5 w-3.5" />}
              CAC Payback
            </button>
          )}
          {metricToUse === 'AVERAGE_TOTAL_SALES' && (
            <button
              className={`px-3 py-1.5 text-sm rounded-md border flex items-center gap-1.5 ${cumulative ? 'bg-primary text-primary-foreground border-primary' : 'bg-background hover:bg-muted border-border'}`}
              onClick={() => setCumulative(!cumulative)}
            >
              {cumulative ? <Eye className="h-3.5 w-3.5" /> : <EyeOff className="h-3.5 w-3.5" />}
              Cumulative
            </button>
          )}
          <div className="flex rounded-md border border-border overflow-hidden">
            <button
              className={`px-3 py-1.5 text-sm ${metricToUse === 'AVERAGE_TOTAL_SALES' ? 'bg-primary text-primary-foreground' : 'bg-background hover:bg-muted'}`}
              onClick={() => setMetricToUse('AVERAGE_TOTAL_SALES')}
              title="Average Total Sales"
            >
              $
            </button>
            <button
              className={`px-3 py-1.5 text-sm border-l border-border ${metricToUse === 'NUMBER_OF_CUSTOMERS' ? 'bg-primary text-primary-foreground' : 'bg-background hover:bg-muted'}`}
              onClick={() => setMetricToUse('NUMBER_OF_CUSTOMERS')}
              title="# of Purchasers"
            >
              #
            </button>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="w-full overflow-x-auto">
        <table className="w-full border-collapse text-sm">
          <thead>
            <tr>
              <th className="p-2 border border-border bg-muted text-left font-medium">Cohort</th>
              <th className="p-2 border border-border bg-muted text-right font-medium">#</th>
              <th className="p-2 border border-border bg-muted text-right font-medium">RPR</th>
              {includeCAC && <th className="p-2 border border-border bg-muted text-right font-medium">CAC</th>}
              {periodHeaders.map((p, idx) => (
                <th key={idx} className="p-2 border border-border bg-muted text-center font-medium">
                  Period {p}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, rowIdx) => {
              let foundPayback = false;
              return (
                <tr key={rowIdx}>
                  <th className="p-2 border border-border bg-muted text-left font-medium">
                    {formatDateValue(row[0].ACQUISITION_COHORT, dateFormat)}
                  </th>
                  <th className="p-2 border border-border bg-muted text-right font-normal">
                    {summarizeNumber(row[0].NUMBER_OF_CUSTOMERS_IN_COHORT)}
                  </th>
                  <th className="p-2 border border-border bg-muted text-right font-normal">
                    {formatValue({ value: repurchaseRateByCohort[row[0].ACQUISITION_COHORT], format: 'PERCENT' })}
                  </th>
                  {includeCAC && (
                    <th className="p-2 border border-border bg-muted text-right font-normal">
                      {formatValue({ value: cacByCohort[row[0].ACQUISITION_COHORT], format: 'FINANCIAL' })}
                    </th>
                  )}
                  {row.map((r, idx) => {
                    const currentValue = metricToUse === 'NUMBER_OF_CUSTOMERS'
                      ? r.NUMBER_OF_CUSTOMERS
                      : (cumulative ? r.CUMULATIVE_VALUE : parseFloat(r.AVERAGE_TOTAL_SALES));
                    const bgColor = generateHeatmapColor(globalMin, globalMax, currentValue);

                    // Calculate CAC payback position
                    let cacPercentage = -1;
                    if (includeCAC && !foundPayback && showCacPayback) {
                      const cac = cacByCohort[row[0].ACQUISITION_COHORT];
                      const cumulativeLTV = r.CUMULATIVE_VALUE;
                      if (cumulativeLTV >= cac) {
                        foundPayback = true;
                        let previousVal = 0;
                        if (row[idx - 1]) {
                          previousVal = row[idx - 1].CUMULATIVE_VALUE;
                        }
                        const cacDiff = cac - previousVal;
                        const range = cumulativeLTV - previousVal;
                        cacPercentage = cacDiff / range;
                      }
                    }

                    return (
                      <td
                        key={idx}
                        className="p-2 border border-border text-center text-white relative"
                        style={{ backgroundColor: bgColor }}
                      >
                        {metricToUse === 'NUMBER_OF_CUSTOMERS' && summarizeNumber(r.NUMBER_OF_CUSTOMERS)}
                        {metricToUse === 'AVERAGE_TOTAL_SALES' && (
                          cumulative
                            ? formatValue({ value: r.CUMULATIVE_VALUE, format: 'FINANCIAL' })
                            : formatValue({ value: parseFloat(r.AVERAGE_TOTAL_SALES), format: 'FINANCIAL' })
                        )}
                        {includeCAC && foundPayback && cacPercentage >= 0 && (
                          <div
                            className="absolute top-0 h-full w-1 bg-orange-500"
                            style={{ left: `${cacPercentage * 100}%` }}
                          />
                        )}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

